import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  orderBy,
  doc,
  where,
  getDoc,
  writeBatch,
  getDocs,
  addDoc,
  endAt,
  startAt,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../hooks/AuthenticationProvider";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ResidentOverview from "../components/ResidentOverview";
import Calendar from "../components/Calendar";
import BudgetTab from "../components/BudgetTab";
import ResidentPhases from "../components/ResidentPhases";
import {
  LineChart,
  Line,
  CartesianGrid,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import {
  Home,
  Moon,
  FileText,
  Calendar as CalendarIcon,
  User,
  DollarSign,
  HeartPulse,
  Save,
  Eye,
  Edit2,
  UserCircle,
  Filter,
  Zap,
  TestTube,
  ClipboardList,
  X,
  Plus,
  Download,
  CheckCircle,
  Menu,
  ChevronRight,
} from "lucide-react";
import FormRenderer from "../components/FormRenderer";
import Select from "react-select";
import { findMedicationSuggestions } from "../utils/medicationList";

const DomainScoreCard = ({
  label,
  score,
  colorClass,
  borderClass,
  textClass,
}) => (
  <div
    className={`relative bg-gradient-to-b ${colorClass} backdrop-blur-xl rounded-xl border ${borderClass} p-4`}
  >
    <h3
      className={`text-base md:text-lg font-medium ${textClass} mb-1 md:mb-2`}
    >
      {label}
    </h3>
    <div className="flex items-end gap-2">
      <p className="text-xl md:text-2xl font-bold text-white">{score ?? "-"}</p>
    </div>
  </div>
);

const TimeCard = ({
  title,
  time,
  date,
  icon: Icon,
  gradientFrom,
  gradientTo,
}) => (
  <div
    className="relative overflow-hidden rounded-lg flex-1 min-w-[150px]"
    style={{
      background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
    }}
  >
    <div className="p-3 relative">
      <div className="flex items-center gap-2">
        <Icon className="text-white/80" size={16} />
        <h3 className="text-sm font-medium text-white/80">{title}</h3>
      </div>
      <div className="mt-1">
        <p className="text-lg font-bold text-white">{time}</p>
        <p className="text-xs text-white/60">
          Since {new Date(date).toLocaleDateString()}
        </p>
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "black",
          padding: "10px",
          borderRadius: "5px",
        }}
      >
        <p className="label" style={{ color: "white" }}>{`${label}`}</p>
        {payload.map((entry, index) => (
          <p key={`item-${index}`} style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ActionButton = ({ icon: Icon, label, onClick, badge = null }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 w-full p-3 md:p-4 bg-slate-800/50 hover:bg-slate-800
    rounded-xl border border-slate-700 transition-all group relative"
  >
    <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
      <Icon className="text-blue-400" size={18} />
    </div>
    <span className="text-white text-sm md:text-base font-medium">{label}</span>
    {badge !== null && (
      <div className="absolute top-1 right-1 md:top-2 md:right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
        {badge}
      </div>
    )}
  </button>
);

const calculateDuration = (startDate, endDate) => {
  startDate = new Date(startDate);
  endDate = new Date(endDate);

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  let result = "";
  if (years > 0) result += `${years}y `;
  if (months > 0) result += `${months}m `;
  if (days > 0) result += `${days}d `;

  return result.trim();
};

const getLengthOfStay = (todaysDate, dischargeDate) => {
  const startDate = new Date(todaysDate);
  const endDate = dischargeDate ? new Date(dischargeDate) : new Date();
  return calculateDuration(startDate, endDate);
};

const calculateLengthOfSobriety = (sobrietyDate) => {
  if (!sobrietyDate && sobrietyDate !== "") return null;
  const now = new Date();
  const startDate = new Date(sobrietyDate);
  return calculateDuration(startDate, now);
};

const ResidentDashboard = () => {
  const {
    user,
    role,
    checkPermission,
    organizationId,
    organizationData,
    isLoading: orgLoading,
    checkFeature,
  } = useAuth();
  const navigate = useNavigate();
  // State management for modals and UI
  const [uaResultsModalOpen, setUaResultsModalOpen] = useState(false);
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [overnightModalOpen, setOvernightModalOpen] = useState(false);
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showMATStatusModal, setShowMATStatusModal] = useState(false);
  const [showProbationStatusModal, setShowProbationStatusModal] =
    useState(false);
  const [newMATDate, setNewMATDate] = useState("");
  const [newProbationDate, setNewProbationDate] = useState("");
  const [matNotes, setMATNotes] = useState("");
  const [probationNotes, setProbationNotes] = useState("");
  const [pendingMATChange, setPendingMATChange] = useState(null);
  const [pendingProbationChange, setPendingProbationChange] = useState(null);
  const [weeklyMeetingCount, setWeeklyMeetingCount] = useState(0);
  const [showAccommodationForm, setShowAccommodationForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [availableSubstances, setAvailableSubstances] = useState({});
  const [isMyInfoModalOpen, setIsMyInfoModalOpen] = useState(false);
  const [editableProfile, setEditableProfile] = useState(null); // Initialize as null
  const [medicationSuggestions, setMedicationSuggestions] = useState([]);
  const [activeMedicationIndex, setActiveMedicationIndex] = useState(null);
  const [isLoadingMyInfo, setIsLoadingMyInfo] = useState(false);
  // State for resident-specific data
  const [chartType, setChartType] = useState("domainScores");
  const [selectedDomain, setSelectedDomain] = useState("domainScores");
  const [residentData, setResidentData] = useState(null);
  const [uaResults, setUaResults] = useState([]);
  const [meetingName, setMeetingName] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [latestQuestionnaire, setLatestQuestionnaire] = useState(null);
  const [allQuestionnaires, setAllQuestionnaires] = useState([]);
  const [allUaResults, setAllUaResults] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [allForms, setAllForms] = useState([]);
  const [completedForms, setCompletedForms] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [employer, setEmployer] = useState("");
  const [employmentNotes, setEmploymentNotes] = useState("");
  const [newEmploymentDate, setNewEmploymentDate] = useState("");
  const [pendingWorkStatusChange, setPendingWorkStatusChange] = useState(null);
  const [editingEmploymentIndex, setEditingEmploymentIndex] = useState(null);
  const [editingEmploymentRecord, setEditingEmploymentRecord] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewingForm, setViewingForm] = useState(false);
  const [viewingFormSubmission, setViewingFormSubmission] = useState(null);
  const [isStaffViewing, setIsStaffViewing] = useState(false);
  const [activeDocTab, setActiveDocTab] = useState("pending");
  const [checkInType, setCheckInType] = useState("meeting");
  const [existingCheckIn, setExistingCheckIn] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState("in");
  const [isScheduleLoaded, setIsScheduleLoaded] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [accommodationRequest, setAccommodationRequest] = useState({
    fullName: "",
    phoneNumber: "",
    disabilityNature: "",
    requestDetails: "",
  });
  const [dailyCheckIns, setDailyCheckIns] = useState([]);
  const [overnightRequest, setOvernightRequest] = useState({
    startDate: "",
    endDate: "",
    location: "",
    address: "",
    contingencyPlan: "",
  });

  const getStrokeColor = (index) => {
    const colors = [
      "#8884d8",
      "#82ca9d",
      "#ffc658",
      "#ff7300",
      "#413ea0",
      "#E91E63",
      "#673AB7",
      "#00BCD4",
      "#4CAF50",
      "#FFC107",
    ];
    return colors[index % colors.length];
  };

  const domainQuestions = {
    General: [
      "criminalJustice",
      "employed",
      "sponsor",
      "lastStepCompleted",
      "totalRecoveryTime",
      "educationLevel",
      "lifeSatisfaction",
      "jailExperienceEffect",
      "adverseExperiences",
      "therapy",
      "reliableTransportation", // Add this if missing
    ],
    Social: [
      "supportOthers",
      "friendsInRecovery",
      "authenticSelf", // Make sure this matches your actual question ID
      "feelOutcast",
      "healthySocialGroup",
      "rebuiltRelationships",
      "strongRelationKids", // These are conditional - make sure they exist
      "strongRelationFamily",
      "familyDysfunction",
      "healthyBoundaries", // Add this if missing
      "partOfCommunity",
    ],
    Physical: [
      "bodyImage",
      "enoughMoney",
      "pain",
      "motivation",
      "medicalInsurance",
      "generalHygeine", // Check spelling - might be "generalHygiene"
      "workoutRegularly",
      "eatHealthy",
      "sleep",
      "physicallyHealthy",
    ],
    Individual: [
      "hardToHaveFun",
      "guiltOrShame",
      "decisionMakingAbilities",
      "selfImprovement",
      "mentalHealthProblems",
      "lifeHasPurpose",
      "hardToTrust",
      "cravings", // ← Fixed: was "maintainRecovery"
      "emotionalRegulation",
      "hardToLetGo",
    ],
    Spiritual: [
      "spirituallyConnected",
      "spiritualGrowth",
      "innerPeace",
      "forgiveness",
      "spiritualPractices",
      "greaterPurpose",
      "deepGratitude",
      "selfReflection",
      "moralConviction",
      "happyFuture", // Add this if missing
    ],
  };

  useEffect(() => {
    if (isMyInfoModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMyInfoModalOpen]);

  useEffect(() => {
    // Check if this is a Leader viewing their own dashboard
    const isLeaderViewingSelf =
      role === "Leader" && sessionStorage.getItem("viewingResidentId");

    if (isLeaderViewingSelf) {
      console.log("Leader viewing own dashboard - using special data loading");
      // Disable notification fetching for leaders viewing their own dashboard
      sessionStorage.setItem("suppressNotifications", "true");
    } else {
      // Remove the suppression flag when not needed
      sessionStorage.removeItem("suppressNotifications");
    }

    return () => {
      // Cleanup: Always remove the flag when component unmounts
      sessionStorage.removeItem("suppressNotifications");
    };
  }, [role]);

  useEffect(() => {
    const fetchResidentData = async () => {
      // Check if we're viewing as admin/staff (from resident profile)
      const viewingResidentId = sessionStorage.getItem("viewingResidentId");
      const viewingResidentName = sessionStorage.getItem("viewingResidentName");

      if (viewingResidentId && organizationId) {
        try {
          // Fetch the specific resident by ID
          const residentDocRef = doc(
            db,
            "organizations",
            organizationId,
            "residents",
            viewingResidentId
          );
          const residentDocSnap = await getDoc(residentDocRef);

          if (residentDocSnap.exists()) {
            const residentData = residentDocSnap.data();
            setResidentData({
              id: viewingResidentId,
              ...residentData,
            });

            // Remove payment history fetch
          } else {
            console.error("Resident document not found");
          }
        } catch (error) {
          console.error("Error fetching resident data:", error);
        }
      }
      // If not viewing as admin/staff, use the original logic to find the resident based on the logged-in user
      else if (user?.email && organizationId) {
        try {
          const userDocRef = doc(
            db,
            "organizations",
            organizationId,
            "users",
            user.email
          );
          const userDocSnap = await getDoc(userDocRef);

          if (!userDocSnap.exists()) {
            console.error("User document not found");
            return;
          }

          const userDetails = userDocSnap.data();
          console.log("User Details:", userDetails);

          // Find the resident in organizationData.residents
          const resident = organizationData.residents.find(
            (r) =>
              r.firstName === userDetails.firstName &&
              r.lastName === userDetails.lastName &&
              r.currentResident === true
          );

          if (resident) {
            console.log("Resident Found:", resident);
            setResidentData({
              id: resident.id,
              ...resident,
              firstName: userDetails.firstName,
              lastName: userDetails.lastName,
            });

            // Remove payment history fetch
          } else {
            console.error("Resident not found in organizationData.residents");
          }
        } catch (error) {
          console.error("Error fetching resident data:", error);
        }
      }
    };

    fetchResidentData();
  }, [user, organizationId, organizationData]);

  // Combined useEffect to fetch both documents and today's schedule on initial render
  useEffect(() => {
    if (residentData?.id && organizationId) {
      // Reset loading state when we start a new fetch
      setIsScheduleLoaded(false);

      // Fetch pending documents for badge
      console.log("Eagerly fetching documents data on initial render");
      fetchDocuments();

      // Fetch today's schedule for the Overview tab
      if (checkFeature("schedule", "resident")) {
        console.log("Eagerly fetching TODAY'S schedule data on initial render");
        fetchSchedule(true); // Pass true to indicate we only want today's events
      }

      // Fetch resident's check-ins for today
      fetchDailyCheckIns();
    }
  }, [residentData, organizationId]);

  useEffect(() => {
    const viewingResidentId = sessionStorage.getItem("viewingResidentId");
    if (viewingResidentId) {
      setIsStaffViewing(true);
    }
  }, []);

  const fetchDailyCheckIns = async () => {
    if (!organizationId || !residentData) return;

    try {
      // Get today's date range
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      // Query check-ins for today
      const checkInsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/checkIns`
      );

      const q = query(
        checkInsRef,
        where("timestamp", ">=", startOfDay),
        where("timestamp", "<=", endOfDay)
      );

      const snapshot = await getDocs(q);
      const todayCheckIns = snapshot.docs.map((doc) => {
        const data = doc.data();
        const checkInTime = data.timestamp?.toDate
          ? data.timestamp.toDate()
          : new Date(data.timestamp);
        const checkOutTime = data.checkOutTime?.toDate
          ? data.checkOutTime.toDate()
          : data.checkOutTime
          ? new Date(data.checkOutTime)
          : null;

        // Calculate duration and determine if it meets minimum requirement
        let duration = null;
        let meetsMinimumDuration = false;

        if (checkOutTime && checkInTime) {
          duration =
            (checkOutTime.getTime() - checkInTime.getTime()) / (1000 * 60); // duration in minutes
          meetsMinimumDuration = duration >= 45; // 45 minute minimum
        }

        return {
          id: doc.id,
          ...data,
          timestamp: checkInTime,
          checkOutTime,
          duration,
          meetsMinimumDuration,
          isCheckIn: true, // Flag to identify check-ins
        };
      });

      // Find any check-ins that don't have checkOutTime
      const pendingCheckIns = todayCheckIns.filter(
        (checkIn) => !checkIn.checkOutTime && checkIn.type === "meeting"
      );

      if (pendingCheckIns.length > 0) {
        setExistingCheckIn(pendingCheckIns[0]);
      } else {
        setExistingCheckIn(null);
      }

      setDailyCheckIns(todayCheckIns);

      // Also fetch the weekly meeting count
      const weeklyCount = await getWeeklyMeetingAttendance();
      setWeeklyMeetingCount(weeklyCount);
    } catch (error) {
      console.error("Error fetching daily check-ins:", error);
    }
  };

  // Function to get weekly meeting attendance count
  const getWeeklyMeetingAttendance = async () => {
    if (!organizationId || !residentData) return 0;

    try {
      // Calculate the start and end of the current week (Sunday to Saturday)
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, etc.

      // Get the date of the start of this week (Sunday)
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);

      // Get the date of the end of this week (Saturday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      // Query check-ins for the current week
      const checkInsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/checkIns`
      );

      const q = query(
        checkInsRef,
        where("timestamp", ">=", startOfWeek),
        where("timestamp", "<=", endOfWeek),
        where("type", "==", "meeting") // Only count check-ins with type "meeting"
      );

      const snapshot = await getDocs(q);

      // Map check-ins to their days and validate duration (to count only one valid meeting per day)
      const daysWithValidMeetings = new Set();

      snapshot.docs.forEach((doc) => {
        const checkIn = doc.data();

        // Convert timestamp to date
        const timestamp = checkIn.timestamp.toDate
          ? checkIn.timestamp.toDate()
          : new Date(checkIn.timestamp);

        // Check if there's a checkout time and calculate duration
        let isValidMeeting = false;

        if (checkIn.checkOutTime) {
          const checkOutTime = checkIn.checkOutTime.toDate
            ? checkIn.checkOutTime.toDate()
            : new Date(checkIn.checkOutTime);

          const duration =
            (checkOutTime.getTime() - timestamp.getTime()) / (1000 * 60); // duration in minutes
          isValidMeeting = duration >= 45; // Must be 45+ minutes
        }
        // If no checkout time yet, we don't count it (they're still checked in or didn't check out properly)

        // Only count if it's a valid meeting (45+ minutes)
        if (isValidMeeting) {
          const dateString = timestamp.toISOString().split("T")[0];
          daysWithValidMeetings.add(dateString);
        }
      });

      // Return the count of unique days with valid meetings
      return daysWithValidMeetings.size;
    } catch (error) {
      console.error("Error fetching weekly meeting attendance:", error);
      return 0;
    }
  };

  const fetchDocuments = async () => {
    if (!organizationId || !residentData) return;

    try {
      // 1) Fetch all forms from the organization
      const formsRef = collection(db, `organizations/${organizationId}/forms`);
      const formsSnapshot = await getDocs(formsRef);
      const forms = formsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Fetched forms:", forms);

      // Store all forms but filter for dashboard display
      setAllForms(forms);

      // Get forms that should appear on dashboard (excluding application forms)
      const dashboardForms = forms.filter(
        (form) => form.showOnDashboard === true && !form.isApplication
      );

      // 2) Fetch calendar events with attached forms for today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const scheduleRef = collection(
        db,
        `organizations/${organizationId}/schedule`
      );
      const eventsQuery = query(
        scheduleRef,
        where("hasAttachedForm", "==", true)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      // Filter events that are happening today
      const todayEvents = eventsSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((event) => {
          // Handle recurring events
          if (event.frequency) {
            const eventDay = parseInt(event.dayOfWeek);
            return eventDay === today.getDay();
          }

          // Handle one-time events
          if (event.startDate) {
            const eventDate = new Date(event.startDate.seconds * 1000);
            return eventDate >= today && eventDate < tomorrow;
          }

          return false;
        });

      console.log("Today's events with forms:", todayEvents);

      // 3) Figure out if any RECURRING forms are due today
      const recurringFormsForToday = dashboardForms.filter((form) => {
        if (!form.isReoccurringForm) return false;

        if (form.reoccurrenceType === "weekly") {
          // Check if today matches the day of week (0-6, Sunday to Saturday)
          return parseInt(form.reoccurrenceDay) === today.getDay();
        } else if (form.reoccurrenceType === "monthly") {
          // Check if today matches the day of month (1-31)
          return parseInt(form.reoccurrenceDay) === today.getDate();
        }

        return false;
      });

      console.log("Today's recurring forms:", recurringFormsForToday);

      // 4) Fetch resident's form submissions (regular & application)
      const residentFullName = `${residentData.firstName} ${residentData.lastName}`;
      const submissionsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/formSubmissions`
      );
      const submissionsSnapshot = await getDocs(submissionsRef);

      const regularSubmissions = submissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Also fetch application submissions for this resident
      const applicationsRef = collection(
        db,
        `organizations/${organizationId}/applications`
      );
      const appQuery = query(
        applicationsRef,
        where("residentFullName", "==", residentFullName)
      );
      const applicationsSnapshot = await getDocs(appQuery);

      const applicationSubmissions = applicationsSnapshot.docs.map((doc) => {
        const appData = doc.data();
        return {
          id: doc.id,
          ...appData,
          formName: appData.formName || "Application Form",
          isApplication: true,
        };
      });

      // Combine both types of submissions
      const allSubmissions = [...regularSubmissions, ...applicationSubmissions];
      console.log("All submissions:", allSubmissions);

      // 4B) ALSO fetch the resident's file uploads (to merge into "completed" docs):
      const fileUploadsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/fileUploads`
      );
      const fileUploadsSnap = await getDocs(fileUploadsRef);
      const fileUploadsData = fileUploadsSnap.docs.map((uploadDoc) => ({
        id: uploadDoc.id,
        type: "fileUpload",
        ...uploadDoc.data(),
      }));

      // Merge form submissions + file uploads into our "completed" array:
      const combinedCompleted = [...allSubmissions, ...fileUploadsData];
      setCompletedForms(combinedCompleted);

      // 5) Identify which NON‐recurring forms have EVER been submitted
      //    (i.e., if sub is not from an event & not from a recurring frequency)
      //    so we know the resident doesn't need to see it again on "Pending."
      const nonRecurringCompletedFormIds = new Set();
      for (const sub of allSubmissions) {
        const isEventSubmission = !!sub.eventId; // event-based submission?
        const isRecurringSubmission = !!sub.formFrequency; // daily/weekly/monthly?
        if (!isEventSubmission && !isRecurringSubmission) {
          // It's effectively a "one-time" doc that was submitted at least once
          nonRecurringCompletedFormIds.add(sub.formId);
        }
      }

      // 6) Now figure out which submissions are "recent" for daily/weekly/monthly checks
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);

      // Filter for current week (for weekly forms)
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const recentSubmissions = allSubmissions.filter((submission) => {
        const submissionDate = submission.dateSubmitted
          ? new Date(submission.dateSubmitted.seconds * 1000)
          : null;

        if (!submissionDate) return false;

        // For event forms and daily recurring forms, check today's date
        if (submission.eventId || submission.formFrequency === "daily") {
          return submissionDate >= startOfDay && submissionDate <= endOfDay;
        }

        // For weekly recurring forms, check current week
        if (submission.formFrequency === "weekly") {
          return submissionDate >= startOfWeek;
        }

        // For monthly recurring forms, check current month
        if (submission.formFrequency === "monthly") {
          return (
            submissionDate.getMonth() === today.getMonth() &&
            submissionDate.getFullYear() === today.getFullYear()
          );
        }

        // If none of the above, then it's not "recent" in that sense
        return false;
      });

      console.log("Recent form submissions:", recentSubmissions);

      // 7) Build the "pending" list

      // (a) For event forms happening today but not yet submitted
      const pendingEventForms = todayEvents.filter(
        (event) => !recentSubmissions.some((sub) => sub.eventId === event.id)
      );

      // (b) For recurring forms that are due today but not yet submitted
      const pendingRecurringForms = recurringFormsForToday.filter(
        (form) => !recentSubmissions.some((sub) => sub.formId === form.id)
      );

      // (c) For regular, NON-recurring forms that show on dashboard:
      //     skip if it's already in nonRecurringCompletedFormIds
      const regularPendingForms = dashboardForms
        .filter((form) => !form.isReoccurringForm)
        .filter((form) => !nonRecurringCompletedFormIds.has(form.id));

      // 8) Combine them all
      const allPendingForms = [
        // All event-based forms
        ...pendingEventForms.map((event) => ({
          id: event.attachedFormId,
          name: event.attachedFormName,
          eventId: event.id,
          eventTitle: event.title,
          isEventForm: true,
        })),
        // Recurring forms due today
        ...pendingRecurringForms,
        // Normal "once-only" forms that haven't been submitted yet
        ...regularPendingForms,
      ];

      console.log("All pending forms:", allPendingForms);
      setPendingDocuments(allPendingForms);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const fetchSchedule = async (todayOnly = false) => {
    if (!organizationId) return;

    // Reset loading state when starting a fetch
    setIsScheduleLoaded(false);

    try {
      const scheduleRef = collection(
        db,
        `organizations/${organizationId}/schedule`
      );

      // For "todayOnly" we'll just fetch all events and filter them in JS
      // This is more reliable than trying to query Firestore for recurring events
      const q = query(scheduleRef);

      const snapshot = await getDocs(q);

      let fetchedSchedule = snapshot.docs.map((doc) => {
        const data = doc.data();
        const startDate =
          data.startDate instanceof Date
            ? data.startDate
            : data.startDate?.toDate?.() || new Date(data.startDate);
        const endDate =
          data.endDate instanceof Date
            ? data.endDate
            : data.endDate?.toDate?.() || new Date(data.endDate);

        return {
          id: doc.id,
          ...data,
          startDate,
          endDate,
        };
      });

      // Filter for today's events if todayOnly is true
      if (todayOnly) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // This is the key change - filter the results in JS
        fetchedSchedule = fetchedSchedule.filter((event) => {
          // For one-time events, check if the startDate is today
          if (event.startDate && !event.frequency) {
            return event.startDate >= today && event.startDate < tomorrow;
          }

          // For recurring events, check if today matches the dayOfWeek
          if (event.frequency === "weekly" && event.dayOfWeek !== undefined) {
            const todayDayOfWeek = today.getDay();
            return parseInt(event.dayOfWeek) === todayDayOfWeek;
          }

          return false;
        });
      }

      console.log(
        `Fetched ${todayOnly ? "today's" : "all"} schedule:`,
        fetchedSchedule
      );

      // Update the schedule state
      setSchedule(fetchedSchedule);

      // Mark loading as complete
      setIsScheduleLoaded(true);
    } catch (error) {
      console.error("Error fetching schedule:", error);
      // Even on error, mark as loaded to avoid infinite loading state
      setIsScheduleLoaded(true);
    }
  };

  // Add this useEffect to load today's schedule data on initial render
  useEffect(() => {
    // Only fetch today's schedule if it hasn't been loaded yet
    if (schedule.length === 0 && checkFeature("schedule", "resident")) {
      fetchSchedule(true); // Pass true to indicate we only want today's events
    }
  }, []);

  const fetchOvernightRequests = async () => {
    if (!organizationId || !residentData) return;

    try {
      const requestsRef = collection(
        db,
        `organizations/${organizationId}/overnightRequests`
      );
      // Filter by resident ID to only get this resident's overnight requests
      const q = query(
        requestsRef,
        where("residentId", "==", residentData.id),
        orderBy("submittedAt", "desc")
      );

      const snapshot = await getDocs(q);
      const requests = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPendingRequests(requests);
      console.log("Fetched overnight requests:", requests);
    } catch (error) {
      console.error("Error fetching overnight requests:", error);
    }
  };

  useEffect(() => {
    // Only attempt to fetch if we have the necessary data
    if (residentData?.id && organizationId) {
      console.log("Loading initial overnight requests for schedule");
      fetchOvernightRequests();
    }
  }, [residentData, organizationId]);

  const handleMATStatusChange = (newMATStatus) => {
    const currentStatus = editableProfile.isMAT;

    // If changing from true to false (getting off MAT)
    if (currentStatus === true && newMATStatus === false) {
      setPendingMATChange(false);
      setShowMATStatusModal(true);
      setNewMATDate(new Date().toISOString().split("T")[0]); // Default to today
    } else {
      // Direct update for other changes
      handleChange("isMAT", newMATStatus);
    }
  };

  const handlePOFieldChange = (field, value) => {
    // Update the field first
    handleChange(field, value);

    // Check if both PO fields will be empty after this change
    const updatedProfile = {
      ...editableProfile,
      [field]: value,
    };

    const hadPOInfo = editableProfile.poFirstName || editableProfile.poLastName;
    const willHavePOInfo =
      updatedProfile.poFirstName || updatedProfile.poLastName;

    // If they had PO info before but won't after this change (getting off probation)
    if (hadPOInfo && !willHavePOInfo) {
      setPendingProbationChange(true);
      setShowProbationStatusModal(true);
      setNewProbationDate(new Date().toISOString().split("T")[0]); // Default to today
    }
  };

  // Cancel MAT status change
  const cancelMATStatusChange = () => {
    setShowMATStatusModal(false);
    setPendingMATChange(null);
    setNewMATDate("");
    setMATNotes("");
  };

  // Confirm MAT status change and update history
  const confirmMATStatusChange = async () => {
    if (!newMATDate) return;

    try {
      // Create new MAT history record
      const newRecord = {
        date: newMATDate,
        status: pendingMATChange ? "Started MAT" : "Ended MAT",
        notes: matNotes || null,
      };

      // Update MAT history array
      const updatedHistory = [...(editableProfile.matHistory || []), newRecord];

      // Sort by date (newest first)
      updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        isMAT: pendingMATChange,
        matHistory: updatedHistory,
      }));

      // Close modal and reset
      setShowMATStatusModal(false);
      setPendingMATChange(null);
      setNewMATDate("");
      setMATNotes("");
    } catch (error) {
      console.error("Error updating MAT status:", error);
    }
  };

  // Cancel probation status change
  const cancelProbationStatusChange = () => {
    setShowProbationStatusModal(false);
    setPendingProbationChange(null);
    setNewProbationDate("");
    setProbationNotes("");
  };

  // Confirm probation status change and update history
  const confirmProbationStatusChange = async () => {
    if (!newProbationDate) return;

    try {
      // Create new probation history record
      const newRecord = {
        date: newProbationDate,
        status: "Ended Probation/Parole", // Always "Ended" when this modal appears
        notes: probationNotes || null,
      };

      // Update probation history array
      const updatedHistory = [
        ...(editableProfile.probationHistory || []),
        newRecord,
      ];

      // Sort by date (newest first)
      updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        probationHistory: updatedHistory,
      }));

      // Close modal and reset
      setShowProbationStatusModal(false);
      setPendingProbationChange(null);
      setNewProbationDate("");
      setProbationNotes("");
    } catch (error) {
      console.error("Error updating probation status:", error);
    }
  };

  const handleOpenOvernightRequest = async () => {
    setOvernightModalOpen(true);
    setMobileMenuOpen(false);
    // Only reload if we don't have the data yet
    if (pendingRequests.length === 0 && residentData) {
      console.log("Re-fetching overnight requests for modal");
      await fetchOvernightRequests();
    }
  };

  const handleOpenPaymentHistory = async () => {
    setPaymentHistoryModalOpen(true);
    setMobileMenuOpen(false);
    // Only fetch payment data if it hasn't been loaded yet
    if (paymentHistory.length === 0 && residentData) {
      await fetchPaymentHistory(organizationId, residentData.id);
    }
  };

  const handleOpenUAResults = async () => {
    setUaResultsModalOpen(true);
    setMobileMenuOpen(false);

    // Only fetch UA data and substance list if we don't have them yet
    if (
      (uaResults.length === 0 ||
        Object.keys(availableSubstances).length === 0) &&
      residentData
    ) {
      try {
        // Fetch substances first
        const houseSettingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const houseSettingsDoc = await getDoc(houseSettingsRef);

        if (houseSettingsDoc.exists()) {
          const data = houseSettingsDoc.data();
          if (data.substances && Object.keys(data.substances).length > 0) {
            setAvailableSubstances(data.substances);
          } else {
            // Set a default empty object or a default set of all allowed
            // You may want to define some defaults here
            setAvailableSubstances({});
          }
        }

        // Then fetch the UA history
        await fetchHistory(
          `${residentData.firstName} ${residentData.lastName}`
        );
      } catch (err) {
        console.error("Error fetching UA data:", err);
      }
    }
  };

  const handleOpenDocument = async (document, isCompleted) => {
    if (isCompleted) {
      // For completed forms, we can directly use the document
      try {
        // Check if this is an application form (which we flagged in fetchDocuments)
        if (document.isApplication) {
          console.log("Opening completed application form:", document);
          // Just use the document directly since we've already fetched it
          setViewingFormSubmission(document);
        } else {
          // For regular form submissions
          console.log("Opening completed regular form:", document);
          setViewingFormSubmission(document);
        }
      } catch (error) {
        console.error("Error processing form submission:", error);
      }
    } else {
      // For pending forms, fetch the full form structure and open FormRenderer
      try {
        const formRef = doc(
          db,
          `organizations/${organizationId}/forms`,
          document.id
        );
        const formDoc = await getDoc(formRef);

        if (formDoc.exists()) {
          const formData = formDoc.data();
          // Add resident information to the form
          const formWithResidentInfo = {
            ...formData,
            id: document.id,
            residentId: residentData.id,
            residentFullName: `${residentData.firstName} ${residentData.lastName}`,
          };

          setSelectedForm(formWithResidentInfo);
          setViewingForm(true);
        } else {
          console.error("Form not found");
        }
      } catch (error) {
        console.error("Error fetching form:", error);
      }
    }
  };

  const handleOpenDocuments = async () => {
    setDocumentsModalOpen(true);
    // Only fetch documents data if we don't already have it
    if (pendingDocuments.length === 0 && allForms.length === 0) {
      await fetchDocuments();
    }
  };

  const handleOpenEventForm = async (event) => {
    console.log("handleOpenEventForm called with:", event);

    if (!event || !event.hasAttachedForm || !event.attachedFormId) {
      console.error("No valid form attached to this event:", event);
      return;
    }

    try {
      // Get the form structure from Firebase
      const formRef = doc(
        db,
        `organizations/${organizationId}/forms`,
        event.attachedFormId
      );
      const formDoc = await getDoc(formRef);

      if (!formDoc.exists()) {
        console.error("Form not found:", event.attachedFormId);
        return;
      }

      const formData = formDoc.data();

      // Get the resident's full name
      const residentFullName = `${residentData.firstName} ${residentData.lastName}`;

      // Determine date range for checking existing submissions
      // For calendar view (past events), we need to check for submissions on the specific day
      // Modified code with validation
      let eventDate;
      if (event.date) {
        eventDate = new Date(event.date);
      } else if (event.startDate) {
        eventDate = new Date(event.startDate);
      } else {
        // If no date is available, use today's date as fallback
        eventDate = new Date();
        console.log("No date found for event, using current date as fallback");
      }

      // Now create the date range with the valid date
      const startOfDay = new Date(eventDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(eventDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Query for form submissions by this resident for this form on this specific day
      const submissionsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/formSubmissions`
      );

      const q = query(
        submissionsRef,
        where("formId", "==", event.attachedFormId),
        where("dateSubmitted", ">=", startOfDay),
        where("dateSubmitted", "<=", endOfDay)
      );

      const submissionsSnapshot = await getDocs(q);

      if (!submissionsSnapshot.empty) {
        // Form already submitted for this day - show the completed submission
        const submission = submissionsSnapshot.docs[0].data();
        setViewingFormSubmission({
          id: submissionsSnapshot.docs[0].id,
          ...submission,
        });
      } else {
        // Prepare the form for submission
        const eventFormWithContext = {
          ...formData,
          id: event.attachedFormId,
          eventId: event.id,
          eventTitle: event.title,
          eventDate: event.date || event.startDate,
          residentId: residentData.id,
          residentFullName: residentFullName,
        };

        setSelectedForm(eventFormWithContext);
        setViewingForm(true);
      }
    } catch (error) {
      console.error("Error opening event form:", error);
      console.error("Error details:", error.message);
      alert(
        "There was an error opening this form. Please try again or contact support."
      );
    }
  };

  // Function to handle form submission
  const handleFormSubmit = async (submissionData) => {
    try {
      // Add event context if this form is from an event
      if (selectedForm.eventId) {
        submissionData.eventId = selectedForm.eventId;
        submissionData.eventTitle = selectedForm.eventTitle;
        submissionData.eventDate = selectedForm.eventDate;
      }

      // Add recurring form context if applicable
      if (selectedForm.isReoccurringForm) {
        submissionData.isReoccurringForm = true;
        submissionData.reoccurrenceType = selectedForm.reoccurrenceType;
        submissionData.formFrequency = selectedForm.reoccurrenceType;
      }

      submissionData.formId = selectedForm.id;
      // Add the form submission to the resident's formSubmissions subcollection
      await addDoc(
        collection(
          db,
          `organizations/${organizationId}/residents/${residentData.id}/formSubmissions`
        ),
        submissionData
      );

      // Close the form modal
      setViewingForm(false);

      // Refresh documents to update the UI
      fetchDocuments();

      // Show success message
      alert("Form submitted successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(`Error submitting form: ${error.message}`);
    }
  };

  const handleDownloadPDF = () => {
    const doc = generatePDF();
    doc.save(
      `UA_Results_${residentData.firstName}_${
        new Date().toISOString().split("T")[0]
      }.pdf`
    );
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    // Filter results based on date range first
    const filteredResults =
      startDate && endDate
        ? uaResults.filter((result) => {
            const date = new Date(result.date);
            return date >= new Date(startDate) && date <= new Date(endDate);
          })
        : uaResults;

    // Create better formatted table data
    const tableData = filteredResults.map((result) => {
      // Format date nicely
      const date = new Date(result.date);
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Instead of using the summary field directly, create a clear status indicator
      let status = "Clean";

      // Check if any substance failed
      const failedSubstances = [];
      Object.entries(result.results)
        .filter(([substance]) => availableSubstances[substance] === true)
        .forEach(([substance, result]) => {
          if (result.toLowerCase() === "fail") {
            failedSubstances.push(substance);
            status = "Failed"; // Update status if any fail is found
          }
        });

      // If we have failed substances, include them in the status
      if (failedSubstances.length > 0) {
        status = `Failed - ${failedSubstances.join(", ")}`;
      }

      // Create the details column with all substances clearly organized
      let details = "";

      // First list all passed substances
      const passedList = Object.entries(result.results)
        .filter(([_, result]) => result.toLowerCase() === "pass")
        .map(([substance, _]) => substance.toUpperCase())
        .join(", ");

      details += `Passed: ${passedList}`;

      // Then list failed substances with more emphasis
      if (failedSubstances.length > 0) {
        details += `\n\nFailed: ${failedSubstances
          .map((s) => s.toUpperCase())
          .join(", ")}`;
      }

      return [formattedDate, status, details];
    });

    // Check if we have organization header data
    if (
      organizationData?.houseSettings?.headerLogo ||
      organizationData?.houseSettings?.headerTitle ||
      organizationData?.houseSettings?.headerContact
    ) {
      let yPosition = 10;

      // Add logo if exists with full width
      if (organizationData.houseSettings.headerLogo) {
        try {
          // Calculate the width of the page (with margins)
          const pageWidth = doc.internal.pageSize.width - 28; // 14 units margin on each side
          const logoHeight = 30; // Adjusted height for better appearance

          doc.addImage(
            organizationData.houseSettings.headerLogo,
            "JPEG",
            14,
            yPosition,
            pageWidth,
            logoHeight
          );
          yPosition += logoHeight + 8; // Add spacing after logo
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name if exists
      if (organizationData.houseSettings.headerTitle) {
        doc.setFontSize(18); // Larger font for title
        doc.setFont(undefined, "bold");
        doc.text(organizationData.houseSettings.headerTitle, 14, yPosition);
        yPosition += 10;
      }

      // Add contact info if exists
      if (organizationData.houseSettings.headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines =
          organizationData.houseSettings.headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Add divider line
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, doc.internal.pageSize.width - 14, yPosition);
      yPosition += 15; // Add more space after divider

      // Add title with updated position - more prominent
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      const title = `Urinalysis Results for ${residentData.firstName} ${
        residentData.lastName || ""
      }`;
      doc.text(title, 14, yPosition);

      // Add date range if filtering
      if (startDate && endDate) {
        yPosition += 8;
        doc.setFontSize(10);
        doc.setFont(undefined, "italic");
        const dateRangeText = `Date Range: ${new Date(
          startDate
        ).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`;
        doc.text(dateRangeText, 14, yPosition);
      }

      // Add table with updated position and improved styling
      autoTable(doc, {
        head: [["Date & Time", "Status", "Details"]],
        body: tableData,
        startY: yPosition + 12,
        styles: {
          fontSize: 10,
          cellPadding: 5,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 245, 250],
        },
        columnStyles: {
          0: { cellWidth: 45 }, // Date column
          1: { cellWidth: 40, fontStyle: "bold" }, // Status column with clearer label
          2: { cellWidth: "auto" }, // Details column
        },
        willDrawCell: function (data) {
          // Color the Status cell based on the value
          if (data.column.index === 1 && data.cell.text) {
            const cellText = data.cell.text.join("");
            if (cellText.includes("Failed")) {
              data.cell.styles.textColor = [220, 50, 50]; // Red for failed
            } else if (cellText === "Clean") {
              data.cell.styles.textColor = [46, 184, 46]; // Green for clean
            }
          }
        },
      });
    } else {
      // Original code without header
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      const title = `Urinalysis Results for ${residentData.firstName} ${
        residentData.lastName || ""
      }`;
      doc.text(title, 14, 20);

      // Add date range if filtering
      if (startDate && endDate) {
        doc.setFontSize(10);
        doc.setFont(undefined, "italic");
        const dateRangeText = `Date Range: ${new Date(
          startDate
        ).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`;
        doc.text(dateRangeText, 14, 28);
      }

      // Improved table styling
      autoTable(doc, {
        head: [["Date & Time", "Status", "Details"]],
        body: tableData,
        startY: startDate && endDate ? 35 : 30,
        styles: {
          fontSize: 10,
          cellPadding: 5,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 245, 250],
        },
        columnStyles: {
          0: { cellWidth: 45 }, // Date column
          1: { cellWidth: 40, fontStyle: "bold" }, // Status column with clearer label
          2: { cellWidth: "auto" }, // Details column
        },
        willDrawCell: function (data) {
          // Color the Status cell based on the value
          if (data.column.index === 1 && data.cell.text) {
            const cellText = data.cell.text.join("");
            if (cellText.includes("Failed")) {
              data.cell.styles.textColor = [220, 50, 50]; // Red for failed
            } else if (cellText === "Clean") {
              data.cell.styles.textColor = [46, 184, 46]; // Green for clean
            }
          }
        },
      });
    }

    return doc;
  };

  const fetchPaymentHistory = async (organizationId, residentId) => {
    console.log("Fetching payment history for residentId:", residentId);
    if (!organizationId || !residentId) {
      console.error("Missing organizationId or residentId");
      return;
    }

    try {
      // Updated path to fetch from resident's payments subcollection
      const paymentQuery = query(
        collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/payments`
        ),
        orderBy("paymentDate")
      );

      const paymentSnapshot = await getDocs(paymentQuery);

      const payments = paymentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Payments Retrieved:", payments);
      setPaymentHistory(payments);
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };

  // Update the existing function
  const fetchHistory = async (residentName, startDate, endDate) => {
    if (!organizationId || !residentData) return;

    try {
      // Use residentData.id instead of residentId
      const uaResultsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/uaResults`
      );

      let q;

      if (startDate && endDate) {
        const start = new Date(startDate).toISOString();
        const end = new Date(
          new Date(endDate).setHours(23, 59, 59)
        ).toISOString();

        q = query(uaResultsRef, orderBy("date"), startAt(start), endAt(end));
      } else {
        q = query(uaResultsRef, orderBy("date", "desc"));
      }

      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setUaResults(results);
      setAllUaResults(results); // This used for filtering
    } catch (err) {
      console.error("Error fetching UA history:", err);

      setUaResults([]);
      setAllUaResults([]);
    }
  };

  const getQuestionnaires = async () => {
    if (!residentData || !residentData.id) return;

    try {
      // Use the resident's subcollection directly
      const questionnairesCollectionRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentData.id}/recoveryCapital`
      );

      // No need to filter by name since we're accessing the resident's subcollection
      const querySnapshot = await getDocs(questionnairesCollectionRef);

      const questionnairesData = querySnapshot.docs.map((q) => ({
        id: q.id,
        ...q.data(),
      }));

      // Sort questionnaires for latest and all
      const latestSorted = [...questionnairesData].sort(
        (a, b) => b.timestamp.seconds - a.timestamp.seconds
      );
      setLatestQuestionnaire(latestSorted[0]);

      const allSorted = [...questionnairesData].sort(
        (a, b) => a.timestamp.seconds - b.timestamp.seconds
      );
      setAllQuestionnaires(allSorted);
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
    }
  };

  // Handler for maintenance request submission
  const handleAccommodationSubmit = async (e) => {
    e.preventDefault();
    if (!residentData) return;

    try {
      await addDoc(
        collection(db, `organizations/${organizationId}/accommodationRequests`),
        {
          ...accommodationRequest,
          residentId: residentData.id,
          residentName: `${residentData.firstName} ${residentData.lastName}`,
          status: "pending",
          submittedAt: new Date(),
          archived: false,
        }
      );

      setShowAccommodationForm(false);
      setAccommodationRequest({
        fullName: "",
        phoneNumber: "",
        disabilityNature: "",
        requestDetails: "",
      });
    } catch (error) {
      console.error("Error submitting accommodation request:", error);
    }
  };

  const invertWhiteToBlack = async (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        // Create a canvas to process the image
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Draw the image
        ctx.drawImage(img, 0, 0);

        // Always invert for signatures and light images
        ctx.globalCompositeOperation = "difference";
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => {
        console.error("Error loading image for inversion");
        resolve(imageUrl); // Return original on error
      };

      img.src = imageUrl;
    });
  };

  const generateFormPDF = async (formSubmission, organizationData = {}) => {
    // Create new jsPDF document
    const doc = new jsPDF();
    let yPosition = 10;
    const leftMargin = 14;
    const pageWidth = doc.internal.pageSize.width;
    const rightMargin = pageWidth - leftMargin;

    // ===== ORGANIZATION HEADER SECTION =====
    const { houseSettings = {} } = organizationData;
    let { headerLogo, headerTitle, headerContact } = houseSettings;

    // Check if there's a headerImage from form submission
    if (formSubmission.responses && formSubmission.responses.elementMetadata) {
      const headerImageKey = Object.keys(
        formSubmission.responses.elementMetadata
      ).find(
        (key) =>
          formSubmission.responses.elementMetadata[key].type === "headerImage"
      );

      if (headerImageKey && formSubmission.responses.responses) {
        const headerImageData =
          formSubmission.responses.responses[headerImageKey];
        if (
          headerImageData &&
          typeof headerImageData === "object" &&
          headerImageData.imageUrl
        ) {
          headerLogo = headerImageData.imageUrl;
        } else if (typeof headerImageData === "string") {
          headerLogo = headerImageData;
        }
      }
    }

    // Add organization header
    if (headerLogo || headerTitle || headerContact) {
      // Add logo if exists
      if (headerLogo) {
        try {
          const processedLogo = await invertWhiteToBlack(headerLogo);
          const logoWidth = pageWidth - leftMargin * 2;
          const logoHeight = 30;
          doc.addImage(
            processedLogo,
            "PNG",
            leftMargin,
            yPosition,
            logoWidth,
            logoHeight
          );
          yPosition += logoHeight + 8;
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name or fallback
      if (headerTitle) {
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text(headerTitle, pageWidth / 2, yPosition, { align: "center" });
        yPosition += 10;
      } else {
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Organization Form", pageWidth / 2, yPosition, {
          align: "center",
        });
        yPosition += 10;
      }

      // Add contact info if exists
      if (headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines = headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, pageWidth / 2, yPosition, { align: "center" });
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Divider
      doc.setLineWidth(0.5);
      doc.line(leftMargin, yPosition, rightMargin, yPosition);
      yPosition += 10;
    } else {
      // Default header
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("Form Submission", pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      doc.setLineWidth(0.5);
      doc.line(leftMargin, yPosition, rightMargin, yPosition);
      yPosition += 10;
    }

    // ===== FORM TITLE =====
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text(
      formSubmission.formName || "Form Submission",
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );
    yPosition += 15;

    // ===== METADATA SECTION =====
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    const submittedDate =
      formSubmission.dateSubmitted &&
      typeof formSubmission.dateSubmitted === "object" &&
      "seconds" in formSubmission.dateSubmitted
        ? new Date(formSubmission.dateSubmitted.seconds * 1000).toLocaleString()
        : typeof formSubmission.dateSubmitted === "string"
        ? formSubmission.dateSubmitted
        : "Unknown date";

    const submittedByInfo = formSubmission.submittedBy?.email || "Unknown";
    const residentName = formSubmission.residentFullName || "";

    doc.text(`Submitted by: ${submittedByInfo}`, leftMargin, yPosition);
    yPosition += 6;
    doc.text(`Date: ${submittedDate}`, leftMargin, yPosition);
    yPosition += 6;

    if (residentName) {
      doc.text(`Resident: ${residentName}`, leftMargin, yPosition);
      yPosition += 10;
    } else {
      yPosition += 4;
    }

    // ===== PROCESS FORM DATA =====
    const elementMetadata = formSubmission.responses?.elementMetadata || {};
    const elementOrder = formSubmission.responses?.elementOrder || [];
    const responses =
      formSubmission.responses?.responses || formSubmission.responses || {};

    // Prepare for table
    const tableData = [];
    const signatureFields = [];

    const fieldsToProcess =
      elementOrder.length > 0
        ? elementOrder.map((id) => ({ id, metadata: elementMetadata[id] }))
        : Object.entries(elementMetadata).map(([id, metadata]) => ({
            id,
            metadata,
          }));

    for (const { id, metadata } of fieldsToProcess) {
      if (!metadata) continue;
      const value = responses[id];
      const label = metadata.label || id;

      // Skip header images
      if (metadata.type === "headerImage") continue;

      // Signatures go in a separate array
      if (metadata.type === "signature") {
        signatureFields.push({ id, label, value });
        continue;
      }

      // Format display value
      let displayValue = "";
      if (value === undefined || value === null) {
        displayValue = "";
      } else if (metadata.type === "checkbox" || metadata.type === "yesNo") {
        displayValue = value === true ? "Yes" : "No";
      } else if (
        metadata.type === "date" &&
        typeof value === "object" &&
        "seconds" in value
      ) {
        displayValue = new Date(value.seconds * 1000).toLocaleDateString();
      } else if (
        metadata.valueType === "timestamp" &&
        typeof value === "object" &&
        "seconds" in value
      ) {
        displayValue = new Date(value.seconds * 1000).toLocaleDateString();
      } else if (Array.isArray(value)) {
        displayValue = value.join(", ");
      } else {
        displayValue = String(value || "");
      }

      tableData.push([label, displayValue]);
    }

    // ===== ADD FORM FIELDS TABLE =====
    if (tableData.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [["Field", "Value"]],
        body: tableData,
        theme: "grid",
        headStyles: {
          fillColor: [73, 80, 87],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: "auto", fontStyle: "bold" },
          1: { cellWidth: "auto" },
        },
        styles: {
          cellPadding: 5,
          fontSize: 10,
          overflow: "linebreak",
          lineWidth: 0.1,
        },
        margin: { left: leftMargin, right: leftMargin },
      });

      yPosition = doc.lastAutoTable.finalY + 15;
    }

    // ===== HANDLE SIGNATURES =====
    for (const { label, value } of signatureFields) {
      // New page check
      if (yPosition > doc.internal.pageSize.height - 60) {
        doc.addPage();
        yPosition = 20;
      }

      // Label
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text(`${label}:`, leftMargin, yPosition);
      yPosition += 8;

      // Define the bounding box for the signature
      const signatureBoxWidth = pageWidth - leftMargin * 2;
      const signatureBoxHeight = 50; // Smaller box for the signature

      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(255, 255, 255);
      doc.setLineWidth(0.5);

      // Draw the “box” (you can remove if you prefer no box)
      doc.roundedRect(
        leftMargin,
        yPosition,
        signatureBoxWidth,
        signatureBoxHeight,
        2,
        2,
        "S"
      );

      // If we have a signature
      if (value && value.startsWith("data:image/")) {
        try {
          const processedSignature = await invertWhiteToBlack(value);

          // Load image to measure original size
          const img = new Image();
          img.src = processedSignature;
          await img.decode();

          const originalWidth = img.width;
          const originalHeight = img.height;

          // We'll give ourselves a small margin inside the box
          const horizontalPadding = 5;
          const verticalPadding = 5;

          const maxSignatureWidth = signatureBoxWidth - horizontalPadding * 2;
          const maxSignatureHeight = signatureBoxHeight - verticalPadding * 2;

          // Start by constraining to max width
          let finalWidth = maxSignatureWidth;
          let finalHeight = (originalHeight / originalWidth) * finalWidth;

          // If too tall, constrain by height
          if (finalHeight > maxSignatureHeight) {
            finalHeight = maxSignatureHeight;
            finalWidth = (originalWidth / originalHeight) * finalHeight;
          }

          // Center the image inside the box
          const imageX = leftMargin + (signatureBoxWidth - finalWidth) / 2; // horizontally centered
          const imageY = yPosition + (signatureBoxHeight - finalHeight) / 2; // vertically centered

          doc.addImage(
            processedSignature,
            "PNG",
            imageX,
            imageY,
            finalWidth,
            finalHeight
          );
        } catch (error) {
          console.error("Error adding signature to PDF:", error);
          doc.setFontSize(10);
          doc.setFont(undefined, "italic");
          doc.setTextColor(0, 0, 0);
          doc.text(
            "[Error displaying signature]",
            leftMargin + 10,
            yPosition + 15
          );
        }
      } else {
        // No signature
        doc.setFontSize(10);
        doc.setFont(undefined, "italic");
        doc.setTextColor(150, 150, 150);
        doc.text("[No signature provided]", leftMargin + 10, yPosition + 15);
      }

      yPosition += signatureBoxHeight + 20;
    }

    // ===== THANK YOU MESSAGE =====
    const spaceRemaining = doc.internal.pageSize.height - yPosition;
    if (spaceRemaining < 40) {
      doc.addPage();
      yPosition = 40;
    } else {
      yPosition += 20;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "italic");
    doc.text("Thank you for your submission!", pageWidth / 2, yPosition, {
      align: "center",
    });

    // ===== SAVE PDF =====
    const filename = `${
      formSubmission.formName
        ? formSubmission.formName.toLowerCase().replace(/\s+/g, "-")
        : "form-submission"
    }.pdf`;
    doc.save(filename);
  };
  const handleDownloadReceipt = (payment) => {
    if (!residentData) {
      console.error("Resident data not found");
      return;
    }
    const doc = generateReceiptPDF(payment, residentData, organizationData);
    const filename = `receipt_${residentData.lastName}_${new Date(
      payment.paymentDate.toDate()
    )
      .toISOString()
      .slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  const generateReceiptPDF = (payment, resident, organizationData = {}) => {
    const doc = new jsPDF();

    // Check if we have organization header data
    if (
      organizationData?.houseSettings?.headerLogo ||
      organizationData?.houseSettings?.headerTitle ||
      organizationData?.houseSettings?.headerContact
    ) {
      let yPosition = 10;

      // Add logo if exists with full width
      if (organizationData.houseSettings.headerLogo) {
        try {
          // Calculate the width of the page (with margins)
          const pageWidth = doc.internal.pageSize.width - 28; // 14 units margin on each side
          const logoHeight = 30; // Adjusted height for better appearance

          doc.addImage(
            organizationData.houseSettings.headerLogo,
            "JPEG",
            14,
            yPosition,
            pageWidth,
            logoHeight
          );
          yPosition += logoHeight + 8; // Add spacing after logo
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name if exists
      if (organizationData.houseSettings.headerTitle) {
        doc.setFontSize(18); // Larger font for title
        doc.setFont(undefined, "bold");
        doc.text(organizationData.houseSettings.headerTitle, 14, yPosition);
        yPosition += 10;
      }

      // Add contact info if exists
      if (organizationData.houseSettings.headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines =
          organizationData.houseSettings.headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Add divider line
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, doc.internal.pageSize.width - 14, yPosition);
      yPosition += 15; // Add more space after divider

      // Receipt title
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("PAYMENT RECEIPT", doc.internal.pageSize.width / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      // Receipt details
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Receipt #: ${payment.id || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;
      doc.text(
        `Date: ${payment.paymentDate.toDate().toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      if (payment.startDate && payment.endDate) {
        yPosition += 5;
        doc.text(
          `Service Period: ${new Date(
            payment.startDate
          ).toLocaleDateString()} - ${new Date(
            payment.endDate
          ).toLocaleDateString()}`,
          14,
          yPosition
        );
        yPosition += 10;
      }

      // Resident Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Received From:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      // Payment Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Payment Information:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");

      const paymentInfoTable = [
        ["Payment Type:", payment.paymentType || "Cash"],
        ["Amount:", `$${parseFloat(payment.paymentAmount).toFixed(2)}`],
        [
          "Previous Balance:",
          `$${parseFloat(payment.previousBalance).toFixed(2)}`,
        ],
        ["New Balance:", `$${parseFloat(payment.newBalance).toFixed(2)}`],
      ];

      // Add invoice number if exists
      if (payment.invoiceNumber) {
        paymentInfoTable.push(["Invoice Number:", payment.invoiceNumber]);
      }

      // Add invoice week if exists
      if (payment.invoiceWeek) {
        paymentInfoTable.push(["Invoice Week:", payment.invoiceWeek]);
      }

      // Add funding source if exists
      if (payment.fundingSource) {
        paymentInfoTable.push(["Funding Source:", payment.fundingSource]);
      }

      // Generate the table
      autoTable(doc, {
        startY: yPosition,
        body: paymentInfoTable,
        theme: "plain",
        styles: {
          cellPadding: 3,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 40 },
          1: { cellWidth: "auto" },
        },
      });

      // Get the Y position after the table
      yPosition = doc.lastAutoTable.finalY + 20;

      // Add notes if any
      if (payment.paymentNote) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(payment.paymentNote, 180);
        doc.text(noteLines, 14, yPosition);
      }

      // Add thank you message at the bottom
      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(11);
      doc.setFont(undefined, "italic");
      doc.text(
        "Thank you for your payment!",
        doc.internal.pageSize.width / 2,
        yPosition,
        { align: "center" }
      );
    } else {
      // Simple version without header
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("PAYMENT RECEIPT", doc.internal.pageSize.width / 2, 20, {
        align: "center",
      });

      let yPosition = 40;

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Receipt #: ${payment.id || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;
      doc.text(
        `Date: ${payment.paymentDate.toDate().toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Received From:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Payment Information:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");

      const paymentInfoTable = [
        ["Payment Type:", payment.paymentType || "Cash"],
        ["Amount:", `$${parseFloat(payment.paymentAmount).toFixed(2)}`],
        [
          "Previous Balance:",
          `$${parseFloat(payment.previousBalance).toFixed(2)}`,
        ],
        ["New Balance:", `$${parseFloat(payment.newBalance).toFixed(2)}`],
      ];

      if (payment.invoiceNumber) {
        paymentInfoTable.push(["Invoice Number:", payment.invoiceNumber]);
      }

      if (payment.invoiceWeek) {
        paymentInfoTable.push(["Invoice Week:", payment.invoiceWeek]);
      }

      if (payment.fundingSource) {
        paymentInfoTable.push(["Funding Source:", payment.fundingSource]);
      }

      autoTable(doc, {
        startY: yPosition,
        body: paymentInfoTable,
        theme: "plain",
        styles: {
          cellPadding: 3,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 40 },
          1: { cellWidth: "auto" },
        },
      });

      yPosition = doc.lastAutoTable.finalY + 20;

      if (payment.paymentNote) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(payment.paymentNote, 180);
        doc.text(noteLines, 14, yPosition);
      }

      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(11);
      doc.setFont(undefined, "italic");
      doc.text(
        "Thank you for your payment!",
        doc.internal.pageSize.width / 2,
        yPosition,
        { align: "center" }
      );
    }

    return doc;
  };

  // Handler for overnight request submission
  const handleOvernightSubmit = async (e) => {
    e.preventDefault();
    if (!residentData) return;

    try {
      // Get the resident's house directly from residentData
      const residentHouse = residentData.house;

      await addDoc(
        collection(db, `organizations/${organizationId}/overnightRequests`),
        {
          ...overnightRequest,
          residentId: residentData.id,
          residentName: `${residentData.firstName} ${residentData.lastName}`,
          house: residentHouse, // Add the resident's house to the document
          status: "pending",
          submittedAt: new Date(),
          archived: false,
        }
      );

      // Refresh the requests list
      fetchOvernightRequests();

      setOvernightModalOpen(false);
      setOvernightRequest({
        startDate: "",
        endDate: "",
        location: "",
        address: "",
        contingencyPlan: "",
      });
    } catch (error) {
      console.error("Error submitting overnight request:", error);
    }
  };

  const handleOpenMyInfo = async () => {
    setMobileMenuOpen(false);
    setIsMyInfoModalOpen(true);
    setIsLoadingMyInfo(true);

    // Fetch the resident's data for the My Info modal
    if (residentData?.id && organizationId) {
      try {
        const residentDocRef = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentData.id
        );
        const residentDocSnap = await getDoc(residentDocRef);

        if (residentDocSnap.exists()) {
          const data = residentDocSnap.data();
          setEditableProfile({
            dob: data.dob || "",
            email: data.email || "",
            workStatus: data.workStatus || "",
            employmentHistory: data.employmentHistory || [],
            ecFirstName: data.ecFirstName || "",
            ecLastName: data.ecLastName || "",
            ecRelationship: data.ecRelationship || "",
            ecPhoneNumber: data.ecPhoneNumber || "",
            medications:
              data.medications?.filter(
                (med) => med.name.trim() !== "" || med.dose.trim() !== ""
              ) || [],
            isMAT: data.isMAT ?? false,
            sobrietyDate: data.sobrietyDate || "",
            drugOfChoice: data.drugOfChoice || [],
            poFirstName: data.poFirstName || "",
            poLastName: data.poLastName || "",
            county: data.county || "",
            pendingCharges: data.pendingCharges || "",
          });
        }
      } catch (error) {
        console.error("Error fetching resident data for My Info:", error);
        alert("Failed to load your information. Please try again.");
      } finally {
        setIsLoadingMyInfo(false);
      }
    }
  };

  const handleChange = (key, value) => {
    setEditableProfile((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleWorkStatusChange = (newWorkStatus) => {
    const currentStatus = editableProfile.workStatus;

    // If there's any change in status and it's not just initialization to empty
    if (currentStatus !== newWorkStatus && newWorkStatus !== "") {
      setPendingWorkStatusChange(newWorkStatus);
      setShowEmploymentModal(true);
      setNewEmploymentDate(new Date().toISOString().split("T")[0]); // Default to today
    } else {
      // If no significant change or just initialization, update directly
      handleChange("workStatus", newWorkStatus);
    }
  };

  // Add functions to cancel and confirm work status changes
  const cancelWorkStatusChange = () => {
    setShowEmploymentModal(false);
    setPendingWorkStatusChange(null);
    setNewEmploymentDate("");
    setEmployer("");
    setEmploymentNotes("");
  };

  const confirmWorkStatusChange = async () => {
    if (!newEmploymentDate) return;

    try {
      // Create new employment history record
      const newRecord = {
        date: newEmploymentDate,
        status: pendingWorkStatusChange,
        employer: employer || null,
        notes: employmentNotes || null,
      };

      // Update employment history array
      const updatedHistory = [
        ...(editableProfile.employmentHistory || []),
        newRecord,
      ];

      // Sort by date (newest first)
      updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        workStatus: pendingWorkStatusChange,
        employmentHistory: updatedHistory,
      }));

      // Close modal and reset
      setShowEmploymentModal(false);
      setPendingWorkStatusChange(null);
      setNewEmploymentDate("");
      setEmployer("");
      setEmploymentNotes("");
    } catch (error) {
      console.error("Error updating employment status:", error);
    }
  };

  const handleEditEmploymentRecord = (index) => {
    setEditingEmploymentIndex(index);
    setEditingEmploymentRecord({ ...editableProfile.employmentHistory[index] });
  };

  // Add this function to save changes to an employment record
  const handleSaveEmploymentEdit = () => {
    if (editingEmploymentIndex === null || !editingEmploymentRecord) return;

    const updatedHistory = [...editableProfile.employmentHistory];
    updatedHistory[editingEmploymentIndex] = editingEmploymentRecord;

    // Sort by date (newest first)
    updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

    setEditableProfile((prev) => ({
      ...prev,
      employmentHistory: updatedHistory,
    }));

    setEditingEmploymentIndex(null);
    setEditingEmploymentRecord(null);
  };

  // Add this function to cancel editing
  const handleCancelEmploymentEdit = () => {
    setEditingEmploymentIndex(null);
    setEditingEmploymentRecord(null);
  };

  const handleMedicationChange = (index, field, value) => {
    setEditableProfile((prev) => {
      const updatedMedications = [...prev.medications];
      updatedMedications[index] = {
        ...updatedMedications[index],
        [field]: value,
      };

      // Filter out medications with both fields empty
      const cleanedMedications = updatedMedications.filter(
        (med) => med.name.trim() !== "" || med.dose.trim() !== ""
      );

      return {
        ...prev,
        medications: cleanedMedications,
      };
    });
  };

  const addMedication = () => {
    setEditableProfile((prevProfile) => ({
      ...prevProfile,
      medications: [...(prevProfile.medications || []), { name: "", dose: "" }],
    }));
  };

  const handleMedicationNameInput = (e, index) => {
    const value = e.target.value;
    handleMedicationChange(index, "name", value);

    if (value.trim() === "") {
      setMedicationSuggestions([]);
      setActiveMedicationIndex(null);
      return;
    }

    setMedicationSuggestions(findMedicationSuggestions(value));
    setActiveMedicationIndex(index);
  };

  const handleSelectSuggestion = (suggestion, index) => {
    const fullName = `${suggestion.brandName} (${suggestion.genericName})`;
    handleMedicationChange(index, "name", fullName);
    setMedicationSuggestions([]);
    setActiveMedicationIndex(null);
  };

  const handleUpdateProfile = async () => {
  if (!organizationId || !residentData.id) return;

  try {
    // Create a pending profile update document
    const profileUpdateRef = await addDoc(
      collection(db, `organizations/${organizationId}/profileUpdates`),
      {
        residentId: residentData.id,
        residentName: `${residentData.firstName} ${residentData.lastName}`,
        updatedFields: editableProfile,
        previousFields: {
          dob: residentData.dob || "",
          email: residentData.email || "",
          workStatus: residentData.workStatus || "",
          employmentHistory: residentData.employmentHistory || [],
          ecFirstName: residentData.ecFirstName || "",
          ecLastName: residentData.ecLastName || "",
          ecRelationship: residentData.ecRelationship || "",
          ecPhoneNumber: residentData.ecPhoneNumber || "",
          medications: residentData.medications || [],
          isMAT: residentData.isMAT ?? false,
          matHistory: residentData.matHistory || [], // Add this line
          sobrietyDate: residentData.sobrietyDate || "",
          drugOfChoice: residentData.drugOfChoice || [],
          poFirstName: residentData.poFirstName || "",
          poLastName: residentData.poLastName || "",
          probationHistory: residentData.probationHistory || [], // Add this line
          county: residentData.county || "",
          pendingCharges: residentData.pendingCharges || "",
        },
        status: "pending",
        submittedAt: new Date(),
        archived: false,
      }
    );

      // Send notifications to all users with updateResidentInfo permission
      const usersRef = collection(db, `organizations/${organizationId}/users`);
      const usersSnapshot = await getDocs(usersRef);

      const batch = writeBatch(db);

      // Create notification for each user with updateResidentInfo permission
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();

        // Check if user has permission
        const canApproveUpdates =
          userData.role === "Owner" || userData.role === "Admin";
        if (canApproveUpdates) {
          const notificationRef = doc(
            collection(
              db,
              `organizations/${organizationId}/users/${userDoc.id}/notifications`
            )
          );

          batch.set(notificationRef, {
            subject: "Profile Update Request",
            note: `${residentData.firstName} ${residentData.lastName} has requested a profile update that requires approval.`,
            sourceType: "profileUpdate",
            residentId: residentData.id,
            residentName: `${residentData.firstName} ${residentData.lastName}`,
            updateRequestId: profileUpdateRef.id,
            date: new Date(),
            createdAt: new Date(),
            isRead: false,
            forceEmail: false,
          });
        }
      }

      await batch.commit();

      // Show success message to the resident
      alert(
        "Thank you for updating your info. Once it's approved you will see the changes."
      );
      setIsMyInfoModalOpen(false);
    } catch (err) {
      console.error("Error creating profile update request:", err);
      alert("Failed to submit your update request. Please try again.");
    }
  };

  const handleSwitchToRecoveryTab = async () => {
    setActiveTab("recovery");
    // Only fetch questionnaire data if we don't have it yet
    if (
      (!latestQuestionnaire || !allQuestionnaires.length) &&
      checkFeature("recoveryCapital", "resident")
    ) {
      await getQuestionnaires();
    }
  };

  const handleSwitchToScheduleTab = () => {
    setActiveTab("schedule");
    // Fetch the full schedule only when the Schedule tab is clicked
    if (checkFeature("schedule", "resident")) {
      fetchSchedule(false); // Pass false to get all events
    }
  };

  const handleFilter = () => {
    if (startDate && endDate) {
      const filteredResults = allUaResults.filter((result) => {
        const resultDate = result.date;
        return resultDate >= startDate && resultDate <= endDate;
      });
      setUaResults(filteredResults);
    } else {
      setUaResults(allUaResults);
    }
  };

  // Handler for daily check-in
  const handleCheckIn = async () => {
    // Add this check at the very top
    if (isCheckingIn) return; // Prevent double clicks
    setIsCheckingIn(true); // Disable the button while processing

    if (!residentData) {
      console.warn("Check-in aborted: Resident data is missing.");
      setIsCheckingIn(false); // Re-enable button
      return;
    }

    // Enhanced geocoding function with multiple fallbacks
    const getAddressFromCoordinates = async (latitude, longitude) => {
      console.log("🌍 Starting geocoding process...");

      // Method 1: Try OpenStreetMap first (most reliable, no API key needed)
      try {
        console.log("🗺️ Trying OpenStreetMap geocoding...");
        const osmUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`;

        const osmResponse = await fetch(osmUrl, {
          headers: {
            "User-Agent": "RecoveryHousePlatform/1.0",
          },
        });

        console.log("📨 OSM response status:", osmResponse.status);

        if (osmResponse.ok) {
          const osmData = await osmResponse.json();
          console.log("📋 OSM response:", osmData);

          if (osmData.display_name) {
            // Clean up the address to make it more readable
            let cleanAddress = osmData.display_name;

            // Try to extract just the street address and city if possible
            if (osmData.address) {
              const parts = [];
              if (osmData.address.house_number && osmData.address.road) {
                parts.push(
                  `${osmData.address.house_number} ${osmData.address.road}`
                );
              } else if (osmData.address.road) {
                parts.push(osmData.address.road);
              }

              if (
                osmData.address.city ||
                osmData.address.town ||
                osmData.address.village
              ) {
                parts.push(
                  osmData.address.city ||
                    osmData.address.town ||
                    osmData.address.village
                );
              }

              if (osmData.address.state) {
                parts.push(osmData.address.state);
              }

              if (parts.length > 0) {
                cleanAddress = parts.join(", ");
              }
            }

            console.log("✅ OpenStreetMap geocoding successful:", cleanAddress);
            return cleanAddress;
          }
        }
      } catch (error) {
        console.error("❌ OpenStreetMap geocoding error:", error);
      }

      // Method 2: Try Google Geocoding API if available
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        console.log("🔑 API Key exists:", !!apiKey);

        if (apiKey) {
          const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;
          console.log("🌐 Trying Google Geocoding...");

          const response = await fetch(geocodeUrl);
          console.log("📨 Google response status:", response.status);

          if (response.ok) {
            const data = await response.json();
            console.log("📋 Google API response:", data);

            if (
              data.status === "OK" &&
              data.results &&
              data.results.length > 0
            ) {
              const address = data.results[0].formatted_address;
              console.log("✅ Google geocoding successful:", address);
              return address;
            }
          }
        }
      } catch (error) {
        console.error("❌ Google geocoding error:", error);
      }

      // Method 3: Try BigDataCloud as backup
      try {
        console.log("☁️ Trying BigDataCloud geocoding...");
        const bdcUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;

        const bdcResponse = await fetch(bdcUrl);
        console.log("📨 BigDataCloud response status:", bdcResponse.status);

        if (bdcResponse.ok) {
          const bdcData = await bdcResponse.json();
          console.log("📋 BigDataCloud response:", bdcData);

          if (bdcData.locality && bdcData.principalSubdivision) {
            const address = `${bdcData.locality}, ${bdcData.principalSubdivision}, ${bdcData.countryName}`;
            console.log("✅ BigDataCloud geocoding successful:", address);
            return address;
          }
        }
      } catch (error) {
        console.error("❌ BigDataCloud geocoding error:", error);
      }

      // Fallback: Return formatted coordinates as a readable address
      const fallbackAddress = `Near ${latitude.toFixed(4)}, ${longitude.toFixed(
        4
      )}`;
      console.log(
        "🆘 All geocoding methods failed, using formatted coordinates:",
        fallbackAddress
      );
      return fallbackAddress;
    };

    // CHECK IF USER IS ALREADY CHECKED IN WHEN TRYING TO CHECK IN AGAIN
    if (existingCheckIn && checkInStatus === "in") {
      alert("You already have an active check-in. Please check out first.");
      // Switch to check-out mode automatically
      setCheckInStatus("out");
      setMeetingName(existingCheckIn.meetingName || "");
      return;
    }

    try {
      // HANDLE CHECK OUT FLOW
      if (existingCheckIn && checkInStatus === "out") {
        try {
          // Get current location
          let checkOutLocation = null;

          if (checkFeature("geoLocation", "resident")) {
            if (!navigator.geolocation) {
              alert("Geolocation is not supported by your browser");
              return;
            }

            const permissionStatus = await navigator.permissions.query({
              name: "geolocation",
            });

            if (permissionStatus.state === "denied") {
              alert(
                "Location access is denied. Please enable location access in your browser settings."
              );
              return;
            }

            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
              });
            });

            const { latitude, longitude } = position.coords;

            // Use enhanced geocoding with fallbacks
            const address = await getAddressFromCoordinates(
              latitude,
              longitude
            );

            // Set checkout location
            checkOutLocation = {
              address,
              latitude,
              longitude,
            };

            console.log(
              "📦 CHECKOUT - Location data to save:",
              checkOutLocation
            );
          }

          // Update both documents (in resident's subcollection and organization-wide)
          const batch = writeBatch(db);

          // Update resident's document
          const residentCheckInRef = doc(
            db,
            `organizations/${organizationId}/residents/${residentData.id}/checkIns`,
            existingCheckIn.id
          );

          batch.update(residentCheckInRef, {
            checkOutTime: new Date(),
            checkOutLocation,
            checkOutNotes: meetingName || "Meeting checkout",
            duration:
              (new Date().getTime() -
                new Date(existingCheckIn.timestamp.seconds * 1000).getTime()) /
              (1000 * 60), // duration in minutes
          });

          // Update organization-wide document if it exists
          if (existingCheckIn.orgDocId) {
            const orgCheckInRef = doc(
              db,
              `organizations/${organizationId}/checkIns`,
              existingCheckIn.orgDocId
            );

            batch.update(orgCheckInRef, {
              checkOutTime: new Date(),
              checkOutLocation,
              checkOutNotes: meetingName || "Meeting checkout",
              duration:
                (new Date().getTime() -
                  new Date(
                    existingCheckIn.timestamp.seconds * 1000
                  ).getTime()) /
                (1000 * 60), // duration in minutes
            });
          }

          await batch.commit();

          // Reset state and close modal
          setCheckInModalOpen(false);
          setMeetingName("");
          setExistingCheckIn(null);
          setCheckInStatus("in");
          setShowConfirmation(true);
          setTimeout(() => setShowConfirmation(false), 3000);

          // Refresh check-ins
          fetchDailyCheckIns();

          return;
        } catch (error) {
          console.error("Error during check-out:", error);
          alert(`Error during check-out: ${error.message}`);
          return;
        }
      }

      // HANDLE CHECK IN FLOW

      // Check if geo-location feature is enabled
      if (!checkFeature("geoLocation", "resident")) {
        // If geo-location is disabled, proceed without location data
        const checkInData = {
          residentId: residentData.id,
          residentName: `${residentData.firstName} ${residentData.lastName}`,
          timestamp: new Date(),
          meetingName: meetingName,
          type: checkInType, // Use selected type
          archived: false,
          checkOutTime: null, // Add field for checkout
          checkOutLocation: null, // Add field for checkout location
        };

        // Save to both locations - CREATE BATCH FIRST
        const batch = writeBatch(db);

        // Create document references
        const residentCheckInRef = doc(
          collection(
            db,
            `organizations/${organizationId}/residents/${residentData.id}/checkIns`
          )
        );

        // Save to organization-wide collection
        const orgCheckInRef = doc(
          collection(db, `organizations/${organizationId}/checkIns`)
        );

        // Add the organization doc ID to the resident's record for later updating
        const orgDocData = { ...checkInData, orgDocId: orgCheckInRef.id };

        // IMPORTANT: Only call batch.set ONCE for each reference
        batch.set(residentCheckInRef, orgDocData);
        batch.set(orgCheckInRef, checkInData);

        await batch.commit();

        setCheckInModalOpen(false);
        setMeetingName("");
        setShowConfirmation(true);
        setTimeout(() => setShowConfirmation(false), 3000);

        // Refresh check-ins to update UI
        fetchDailyCheckIns();

        return;
      }

      // If geo-location is enabled, proceed with location data
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser");
        return;
      }

      const permissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });

      if (permissionStatus.state === "denied") {
        alert(
          "Location access is denied. Please enable location access in your browser settings."
        );
        return;
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude } = position.coords;

      // Use enhanced geocoding with fallbacks
      const address = await getAddressFromCoordinates(latitude, longitude);

      const checkInData = {
        residentId: residentData.id,
        residentName: `${residentData.firstName} ${residentData.lastName}`,
        timestamp: new Date(),
        meetingName: meetingName,
        location: {
          address, // This will now always have some kind of address
          latitude,
          longitude,
        },
        type: checkInType, // Use selected type
        archived: false,
        checkOutTime: null, // Add field for checkout
        checkOutLocation: null, // Add field for checkout location
      };

      console.log("📦 CHECKIN - Final check-in data to save:", checkInData);

      // IMPORTANT: Create a single batch operation
      const batch = writeBatch(db);

      // Create document references
      const residentCheckInRef = doc(
        collection(
          db,
          `organizations/${organizationId}/residents/${residentData.id}/checkIns`
        )
      );

      // Save to organization-wide collection
      const orgCheckInRef = doc(
        collection(db, `organizations/${organizationId}/checkIns`)
      );

      // Add the organization doc ID to the resident's record
      const orgDocData = { ...checkInData, orgDocId: orgCheckInRef.id };

      // IMPORTANT: Only set each document ONCE
      batch.set(residentCheckInRef, orgDocData);
      batch.set(orgCheckInRef, checkInData);

      await batch.commit();

      setCheckInModalOpen(false);
      setMeetingName("");
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);

      // Refresh check-ins to update UI
      fetchDailyCheckIns();
    } catch (error) {
      console.error("Error during check-in:", error);
      alert(`Location error: ${error.message}`);
    } finally {
      // Add this line at the end to re-enable the button
      setIsCheckingIn(false);
    }
  };
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 to-slate-950">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  const viewingResidentId = sessionStorage.getItem("viewingResidentId");
  const isLeaderViewingSelf = role === "Leader" && viewingResidentId;

  // Only block access if neither condition is met
  if (!checkPermission("viewResidentDashboard") && !isLeaderViewingSelf) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 to-slate-950 p-6 text-center">
        <div className="bg-slate-800/50 backdrop-blur-xl p-6 rounded-xl border border-slate-700 max-w-lg">
          <div className="text-red-400 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl text-white font-medium mb-2">Access Denied</h2>
          <p className="text-slate-300">
            You don't have permission to view this page. Please contact your
            administrator.
          </p>
        </div>
      </div>
    );
  }
  // No resident data handler
  if (!residentData) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 to-slate-950 p-6 text-center">
        <div className="bg-slate-800/50 backdrop-blur-xl p-6 rounded-xl border border-slate-700 max-w-lg">
          <div className="text-yellow-400 text-5xl mb-4">ℹ️</div>
          <h2 className="text-xl text-white font-medium mb-2">
            No Profile Found
          </h2>
          <p className="text-slate-300">
            No resident profile found. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 p-3 md:p-6">
      {showConfirmation && (
        <div className="fixed left-1/2 top-8 -translate-x-1/2 z-50">
          <div className="bg-slate-800 text-white px-4 py-3 md:px-6 md:py-4 rounded-xl shadow-xl border border-emerald-500/20 flex items-center gap-3">
            <div className="bg-emerald-500/20 rounded-full p-1.5 md:p-2">
              <CheckCircle className="text-emerald-400" size={16} />
            </div>
            <div>
              <p className="font-medium text-sm md:text-base">
                Check-in Successful
              </p>
              <p className="text-slate-400 text-xs md:text-sm">
                Your attendance has been recorded
              </p>
            </div>
          </div>
        </div>
      )}
      {isStaffViewing && (
        <div className="bg-indigo-900/70 text-white p-2 md:p-3 rounded-lg mb-4 md:mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye size={16} className="hidden xs:block" />
            <span className="text-sm md:text-base">Viewing as staff</span>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem("viewingResidentId");
              sessionStorage.removeItem("viewingResidentName");
              window.history.back(); // Go back to the resident profile
            }}
            className="px-2 py-1 md:px-3 md:py-1 bg-indigo-700 hover:bg-indigo-600 rounded-lg text-xs md:text-sm"
          >
            Return to Profile
          </button>
        </div>
      )}

      {role === "Leader" && (
        <div className="bg-blue-900/70 text-white p-2 md:p-3 rounded-lg mb-4 md:mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User size={16} className="hidden xs:block" />
            <span className="text-sm md:text-base">
              Viewing your resident dashboard
            </span>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem("viewingResidentId");
              sessionStorage.removeItem("viewingResidentName");
              navigate("/"); // Go back to leader dashboard
            }}
            className="px-2 py-1 md:px-3 md:py-1 bg-blue-700 hover:bg-blue-600 rounded-lg text-xs md:text-sm"
          >
            Back to Leader Dashboard
          </button>
        </div>
      )}
      {/* Header Section */}
      <div className="max-w-7xl mx-auto mb-4 md:mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 md:gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">
                Welcome Back, {residentData.firstName}
              </h1>
              <p className="text-slate-400 text-sm md:text-base">
                Your Recovery Dashboard
              </p>
            </div>
            <button
              className="lg:hidden p-2 bg-slate-800/50 rounded-lg relative"
              onClick={toggleMobileMenu}
            >
              <Menu size={22} className="text-white" />
              {pendingDocuments.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingDocuments.length}
                </div>
              )}
            </button>
          </div>
          <div className="flex flex-row gap-2 md:gap-4 mt-3 lg:mt-0">
            <TimeCard
              title="Time in Recovery"
              time={calculateLengthOfSobriety(residentData.sobrietyDate)}
              date={residentData.sobrietyDate}
              icon={HeartPulse}
              gradientFrom="#ef476f"
              gradientTo="#b91c1c"
            />
            <TimeCard
              title="Time in House"
              time={getLengthOfStay(residentData.intakeDate)}
              date={residentData.intakeDate}
              icon={Home}
              gradientFrom="#0ea5e9"
              gradientTo="#1e40af"
            />
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Left Sidebar - Action Buttons (Hidden on mobile unless menu opened) */}
          <div
            className={`lg:block lg:col-span-1 space-y-2 md:space-y-3 ${
              mobileMenuOpen ? "block" : "hidden"
            }`}
          >
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-3 md:p-4">
              <h2 className="font-medium text-white text-lg mb-3">
                Quick Actions
              </h2>
              <div className="space-y-2">
                <ActionButton
                  icon={UserCircle}
                  label="My Info"
                  onClick={handleOpenMyInfo}
                />
                {checkFeature("geoLocation", "resident") && (
                  <ActionButton
                    icon={CheckCircle}
                    label={
                      existingCheckIn ? "Meeting Check-Out" : "Meeting Check-In"
                    }
                    onClick={() => {
                      if (existingCheckIn) {
                        setCheckInStatus("out");
                        setMeetingName(existingCheckIn.meetingName || "");
                      } else {
                        setCheckInStatus("in");
                        setMeetingName("");
                      }
                      setCheckInModalOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    badge={existingCheckIn ? "!" : null}
                  />
                )}
                {checkFeature("overnightRequest") && (
                  <ActionButton
                    icon={Moon}
                    label="Overnight Request"
                    onClick={handleOpenOvernightRequest}
                  />
                )}
                {checkFeature("ua", "resident") && (
                  <ActionButton
                    icon={TestTube}
                    label="UA Results"
                    onClick={handleOpenUAResults}
                  />
                )}
                {checkFeature("payments", "resident") && (
                  <ActionButton
                    icon={DollarSign}
                    label="Payment History"
                    onClick={handleOpenPaymentHistory}
                  />
                )}
                <ActionButton
                  icon={FileText}
                  label="Documents"
                  onClick={handleOpenDocuments}
                  badge={
                    pendingDocuments.length > 0 ? pendingDocuments.length : null
                  }
                />
                <ActionButton
                  icon={Zap}
                  label="Gameroom"
                  onClick={() => {
                    navigate("/gameroom");
                    setMobileMenuOpen(false);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {/* Tab Navigation */}
            <div className="bg-slate-800/30 p-1 rounded-xl mb-4 md:mb-6 overflow-x-auto flex space-x-1">
              <button
                onClick={() => setActiveTab("overview")}
                className={`flex-1 py-2 md:py-3 px-2 rounded-lg text-sm md:text-base transition-colors ${
                  activeTab === "overview"
                    ? "bg-slate-700/80 text-white font-medium"
                    : "text-slate-400"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("budget")}
                className={`flex-1 py-2 md:py-3 px-2 rounded-lg text-sm md:text-base transition-colors ${
                  activeTab === "budget"
                    ? "bg-slate-700/80 text-white font-medium"
                    : "text-slate-400"
                }`}
              >
                Budget
              </button>
              {checkFeature("recoveryCapital", "resident") && (
                <button
                  onClick={handleSwitchToRecoveryTab}
                  className={`flex-1 py-2 md:py-3 px-2 rounded-lg text-sm md:text-base transition-colors ${
                    activeTab === "recovery"
                      ? "bg-slate-700/80 text-white font-medium"
                      : "text-slate-400 !important"
                  }`}
                >
                  <span className="hidden xs:inline">Recovery Capital</span>
                  <span className="xs:hidden">Recovery</span>
                </button>
              )}

              {/* Phases Tab - only show if feature is enabled */}
              {checkFeature("phases", "resident") && (
                <button
                  onClick={() => setActiveTab("phases")}
                  className={`flex-1 py-2 md:py-3 px-2 rounded-lg text-sm md:text-base transition-colors ${
                    activeTab === "phases"
                      ? "bg-slate-700/80 text-white font-medium"
                      : "text-slate-400"
                  }`}
                >
                  Phases
                </button>
              )}
              {checkFeature("schedule", "resident") && (
                <button
                  onClick={handleSwitchToScheduleTab}
                  className={`flex-1 py-2 md:py-3 px-2 rounded-lg text-sm md:text-base transition-colors ${
                    activeTab === "schedule"
                      ? "bg-slate-700/80 text-white font-medium"
                      : "text-slate-400"
                  }`}
                >
                  Schedule
                </button>
              )}
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && (
              <ResidentOverview
                meetings={schedule}
                overnightRequests={pendingRequests}
                dailyCheckIns={dailyCheckIns}
                residentData={residentData}
                onOpenForm={handleOpenEventForm}
                isStaffViewing={isStaffViewing}
                isScheduleLoading={!isScheduleLoaded}
              />
            )}

            {activeTab === "budget" && (
              <BudgetTab
                residentData={residentData}
                organizationId={organizationId}
                organizationData={organizationData}
              />
            )}

            {activeTab === "recovery" &&
              checkFeature("recoveryCapital", "resident") && (
                <div className="space-y-4 md:space-y-6">
                  {/* Domain Score Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    <DomainScoreCard
                      label="General"
                      score={latestQuestionnaire?.generalCapitalScore}
                      colorClass="from-emerald-500/10 to-emerald-500/5"
                      borderClass="border-emerald-500/20"
                      textClass="text-emerald-400"
                    />
                    <DomainScoreCard
                      label="Social"
                      score={latestQuestionnaire?.socialCapitalScore}
                      colorClass="from-blue-500/10 to-blue-500/5"
                      borderClass="border-blue-500/20"
                      textClass="text-blue-400"
                    />
                    <DomainScoreCard
                      label="Physical"
                      score={latestQuestionnaire?.physicalCapitalScore}
                      colorClass="from-yellow-500/10 to-yellow-500/5"
                      borderClass="border-yellow-500/20"
                      textClass="text-yellow-400"
                    />
                    <DomainScoreCard
                      label="Individual"
                      score={latestQuestionnaire?.individualCapitalScore}
                      colorClass="from-purple-500/10 to-purple-500/5"
                      borderClass="border-purple-500/20"
                      textClass="text-purple-400"
                    />
                    <DomainScoreCard
                      label="Spiritual"
                      score={latestQuestionnaire?.spiritualCapitalScore}
                      colorClass="from-pink-500/10 to-pink-500/5"
                      borderClass="border-pink-500/20"
                      textClass="text-pink-400"
                    />
                    <DomainScoreCard
                      label="Total"
                      score={latestQuestionnaire?.recoveryCapitalTotal}
                      colorClass="from-red-500/10 to-red-500/5"
                      borderClass="border-red-500/20"
                      textClass="text-red-400"
                    />
                  </div>

                  {/* Detailed Progress Chart */}
                  <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-4 md:p-6">
                    <h3 className="text-lg font-medium text-white mb-4">
                      Recovery Capital Data
                    </h3>
                    {/* Chart Container */}
                    <div className="h-[300px] md:h-[400px]">
                      <ResponsiveContainer width="100%" height="100%">
                        {allQuestionnaires.length === 1 ? (
                          <BarChart
                            data={allQuestionnaires}
                            margin={{
                              top: 10,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            {/* Bars for domainScores */}
                            {chartType === "domainScores" ? (
                              <>
                                <Bar
                                  dataKey="generalCapitalScore"
                                  fill="#8884d8"
                                  name="General"
                                >
                                  <LabelList
                                    dataKey="generalCapitalScore"
                                    position="top"
                                  />
                                </Bar>
                                <Bar
                                  dataKey="socialCapitalScore"
                                  fill="#82ca9d"
                                  name="Social"
                                >
                                  <LabelList
                                    dataKey="socialCapitalScore"
                                    position="top"
                                  />
                                </Bar>
                                <Bar
                                  dataKey="physicalCapitalScore"
                                  fill="#ffc658"
                                  name="Physical"
                                >
                                  <LabelList
                                    dataKey="physicalCapitalScore"
                                    position="top"
                                  />
                                </Bar>
                                <Bar
                                  dataKey="individualCapitalScore"
                                  fill="#ff7300"
                                  name="Individual"
                                >
                                  <LabelList
                                    dataKey="individualCapitalScore"
                                    position="top"
                                  />
                                </Bar>
                                <Bar
                                  dataKey="spiritualCapitalScore"
                                  fill="#413ea0"
                                  name="Spiritual"
                                >
                                  <LabelList
                                    dataKey="spiritualCapitalScore"
                                    position="top"
                                  />
                                </Bar>
                                <Bar
                                  dataKey="recoveryCapitalTotal"
                                  fill="#ff0000"
                                  name="Total"
                                >
                                  <LabelList
                                    dataKey="recoveryCapitalTotal"
                                    position="top"
                                  />
                                </Bar>
                              </>
                            ) : (
                              // Render Bars for selected domain questions
                              domainQuestions[selectedDomain].map(
                                (questionId, index) => (
                                  <Bar
                                    key={questionId}
                                    dataKey={questionId}
                                    fill={getStrokeColor(index)}
                                    name={questionId}
                                  >
                                    <LabelList
                                      dataKey={questionId}
                                      position="top"
                                    />
                                  </Bar>
                                )
                              )
                            )}
                          </BarChart>
                        ) : (
                          <LineChart
                            data={allQuestionnaires}
                            margin={{
                              top: 10,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            {chartType === "domainScores" ? (
                              <>
                                <Line
                                  type="monotone"
                                  dataKey="generalCapitalScore"
                                  stroke="#10B981"
                                  name="General"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="socialCapitalScore"
                                  stroke="#3B82F6"
                                  name="Social"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="physicalCapitalScore"
                                  stroke="#EAB308"
                                  name="Physical"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="individualCapitalScore"
                                  stroke="#8884d8"
                                  name="Individual"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="spiritualCapitalScore"
                                  stroke="#EC4899"
                                  name="Spiritual"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="recoveryCapitalTotal"
                                  stroke="#EF4444"
                                  name="Total"
                                />
                              </>
                            ) : (
                              // Render Lines for selected domain questions
                              domainQuestions[selectedDomain].map(
                                (questionId, index) => (
                                  <Line
                                    key={questionId}
                                    type="monotone"
                                    dataKey={questionId}
                                    stroke={getStrokeColor(index)}
                                    name={questionId}
                                  />
                                )
                              )
                            )}
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            {activeTab === "phases" && checkFeature("phases", "resident") && (
              <ResidentPhases
                residentData={residentData}
                organizationId={organizationId}
              />
            )}
            {activeTab === "schedule" &&
              checkFeature("schedule", "resident") && (
                <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700 p-3 md:p-6 w-full h-full">
                  <div className="h-full w-full">
                    <Calendar
                      meetings={schedule}
                      organizationId={organizationId}
                      onOpenForm={handleOpenEventForm}
                    />
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
      {checkInModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-4 md:p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-white">
                {existingCheckIn ? "Meeting Check-Out" : "Meeting Check-In"}
              </h3>
              <button
                onClick={() => setCheckInModalOpen(false)}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            <p className="text-slate-300 text-sm md:text-base mb-4 md:mb-6">
              Confirm your {existingCheckIn ? "check-out" : "check-in"}. This
              helps us track your recovery journey.
            </p>

            {/* Weekly Meeting Attendance Counter */}
            <div className="mb-4 bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">
                  Meetings attended this week:
                </span>
                <span className="text-cyan-400 font-semibold text-lg">
                  {weeklyMeetingCount}
                </span>
              </div>
            </div>

            {!existingCheckIn && (
              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-1.5">
                  Check-in type
                </label>
                <select
                  value={checkInType}
                  onChange={(e) => setCheckInType(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2.5 text-white border border-slate-600 focus:border-blue-500 transition-colors text-sm md:text-base"
                >
                  <option value="meeting">Meeting</option>
                  <option value="overnight">Overnight</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-slate-400 text-sm mb-1.5">
                {!existingCheckIn
                  ? checkInType === "meeting"
                    ? "Name of the meeting you are attending"
                    : checkInType === "overnight"
                    ? "Overnight location details"
                    : "Check-in details"
                  : "Meeting name"}
              </label>
              <input
                type="text"
                id="meetingName"
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)}
                className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2.5 text-white border border-slate-600 focus:border-blue-500 transition-colors text-sm md:text-base"
                readOnly={
                  existingCheckIn && existingCheckIn.meetingName ? true : false
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCheckInModalOpen(false)}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleCheckIn}
                disabled={isCheckingIn}
                className={`px-3 py-2 ${
                  isCheckingIn
                    ? "bg-gray-500"
                    : "bg-cyan-500/80 hover:bg-cyan-500"
                } text-white rounded-lg transition-colors flex items-center gap-2 text-sm md:text-base`}
              >
                {isCheckingIn ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    {existingCheckIn ? "Check Out" : "Check In"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Overnight Request Modal */}
      {overnightModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header section */}
            <div className="p-4 md:p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="p-1.5 md:p-2 bg-purple-500/10 rounded-lg">
                    <Moon className="text-purple-400" size={18} />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-white">
                    Overnight Requests
                  </h3>
                </div>
                <button
                  onClick={() => setOvernightModalOpen(false)}
                  className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X
                    className="text-slate-400 hover:text-slate-300"
                    size={18}
                  />
                </button>
              </div>
            </div>

            {/* Scrollable content section */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {/* Existing Requests Section */}
              {pendingRequests.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-base md:text-lg text-white mb-3">
                    Your Requests
                  </h4>
                  <div className="space-y-3">
                    {pendingRequests
                      .filter((request) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0); // Reset time to start of day
                        const endDate = new Date(
                          request.endDate + "T00:00:00Z"
                        ); // Force UTC parsing
                        endDate.setHours(0, 0, 0, 0); // Reset time to start of day

                        return !request.archived && endDate >= today;
                      })
                      .map((request) => (
                        <div
                          key={request.id}
                          className="bg-slate-800/30 rounded-xl p-3 md:p-4 border border-slate-700/50"
                        >
                          <div className="flex justify-between items-start flex-wrap gap-2">
                            <div>
                              <p className="text-white text-sm md:text-base">
                                {/* Fix date display with proper formatting */}
                                {new Date(
                                  request.startDate + "T12:00:00"
                                ).toLocaleDateString()}
                                {" - "}
                                {new Date(
                                  request.endDate + "T12:00:00"
                                ).toLocaleDateString()}
                              </p>
                              <p className="text-slate-400 text-xs md:text-sm mt-1">
                                Location: {request.location}
                              </p>
                              <p className="text-slate-400 text-xs md:text-sm mt-1">
                                Address: {request.address}
                              </p>
                              {request.house && (
                                <p className="text-slate-400 text-xs md:text-sm mt-1">
                                  House: {request.house}
                                </p>
                              )}
                              {request.contingencyPlan && (
                                <p className="text-slate-400 text-xs md:text-sm mt-1">
                                  Plan: {request.contingencyPlan}
                                </p>
                              )}
                            </div>
                            <span
                              className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                request.status === "approved"
                                  ? "bg-green-500/20 text-green-300"
                                  : request.status === "denied"
                                  ? "bg-red-500/20 text-red-300"
                                  : "bg-yellow-500/20 text-yellow-300"
                              }`}
                            >
                              {request.status.charAt(0).toUpperCase() +
                                request.status.slice(1)}
                            </span>
                          </div>
                          {(request.status === "approved" ||
                            request.status === "denied") &&
                            request.reviewNotes && (
                              <p className="text-slate-400 text-xs md:text-sm mt-2 border-t border-slate-700/50 pt-2">
                                Staff Note: {request.reviewNotes}
                              </p>
                            )}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* New Request Form */}
              <form
                id="overnightForm"
                onSubmit={handleOvernightSubmit}
                className="space-y-4"
              >
                <h4 className="text-base md:text-lg text-white mb-3">
                  New Request
                </h4>
                <div className="relative">
                  <input
                    type="date"
                    id="startDate"
                    value={overnightRequest.startDate}
                    onChange={(e) =>
                      setOvernightRequest({
                        ...overnightRequest,
                        startDate: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer text-sm md:text-base"
                  />
                  <label
                    htmlFor="startDate"
                    className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                  >
                    Start Date
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="date"
                    id="endDate"
                    value={overnightRequest.endDate}
                    onChange={(e) =>
                      setOvernightRequest({
                        ...overnightRequest,
                        endDate: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer text-sm md:text-base"
                  />
                  <label
                    htmlFor="endDate"
                    className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                  >
                    End Date
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    id="location"
                    value={overnightRequest.location}
                    onChange={(e) =>
                      setOvernightRequest({
                        ...overnightRequest,
                        location: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer text-sm md:text-base"
                  />
                  <label
                    htmlFor="location"
                    className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                  >
                    Where are you going? (e.g., Mom's house)
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    id="address"
                    value={overnightRequest.address}
                    onChange={(e) =>
                      setOvernightRequest({
                        ...overnightRequest,
                        address: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer text-sm md:text-base"
                  />
                  <label
                    htmlFor="address"
                    className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                  >
                    Address
                  </label>
                </div>

                <div className="relative">
                  <textarea
                    id="contingencyPlan"
                    value={overnightRequest.contingencyPlan}
                    onChange={(e) =>
                      setOvernightRequest({
                        ...overnightRequest,
                        contingencyPlan: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer min-h-[80px] md:min-h-[100px] text-sm md:text-base"
                  />
                  <label
                    htmlFor="contingencyPlan"
                    className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                  >
                    What is your plan if you experience difficulties?
                  </label>
                </div>
              </form>
            </div>

            <div className="p-4 md:p-6 border-t border-slate-700">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setOvernightModalOpen(false)}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="overnightForm"
                  className="px-3 py-2 bg-blue-500/80 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2 text-sm md:text-base"
                >
                  <Plus size={18} />
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* UA Results Modal */}
      {uaResultsModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-blue-500/10 rounded-lg">
                  <TestTube className="text-blue-400" size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-white">
                  UA Results History
                </h3>
              </div>
              <button
                onClick={() => setUaResultsModalOpen(false)}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6">
              {/* Date Filter Section */}
              <div className="bg-slate-800/30 rounded-xl p-3 md:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <label className="block text-slate-400 mb-1 md:mb-2 text-xs md:text-sm">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800/50 text-white rounded-lg border border-slate-700/50 focus:border-blue-500/50 transition-all text-xs md:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 md:mb-2 text-xs md:text-sm">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800/50 text-white rounded-lg border border-slate-700/50 focus:border-blue-500/50 transition-all text-xs md:text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleFilter}
                      className="w-full px-3 py-2 bg-blue-500/80 hover:bg-blue-500 text-white rounded-lg transition-all flex items-center justify-center gap-2 text-xs md:text-sm"
                    >
                      <Filter size={14} />
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>

              {/* Results List */}
              <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-track-slate-800 scrollbar-thumb-slate-700">
                {uaResults.length > 0 ? (
                  uaResults.map((result, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-800/30 rounded-xl p-3 md:p-4 border border-slate-700/50"
                    >
                      <div className="flex justify-between items-start mb-2 md:mb-3 flex-wrap gap-2">
                        <div>
                          <p className="text-white text-sm md:text-base font-medium">
                            {new Date(result.date).toLocaleDateString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </p>
                          <p className="text-slate-400 text-xs md:text-sm mt-1">
                            Summary: {result.summary}
                          </p>
                        </div>
                        <span
                          className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            result.summary === "Clean"
                              ? "bg-green-500/20 text-green-300"
                              : "bg-red-500/20 text-red-300"
                          }`}
                        >
                          {result.summary}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(result.results)
                          .filter(
                            ([substance]) =>
                              availableSubstances[substance] === true
                          )
                          .map(([substance, status]) => (
                            <span
                              key={substance}
                              className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs flex items-center justify-between ${
                                status === "pass"
                                  ? "bg-green-500/10 text-green-300 border border-green-500/20"
                                  : "bg-red-500/10 text-red-300 border border-red-500/20"
                              }`}
                            >
                              <span className="capitalize">{substance}</span>
                              <span className="font-medium">{status}</span>
                            </span>
                          ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6">
                    <p className="text-slate-400 text-sm">No results found</p>
                  </div>
                )}
              </div>

              {/* Actions Section */}
              <div className="flex justify-end pt-3 md:pt-4 border-t border-slate-700/50">
                <button
                  onClick={handleDownloadPDF}
                  className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2 text-sm md:text-base"
                >
                  <Download size={18} />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Accommodation Request Modal */}
      {showAccommodationForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-4 md:p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div className="flex-1 text-center">
                <h3 className="text-lg md:text-xl font-semibold text-white">
                  Reasonable Accommodation Request
                </h3>
              </div>
              <button
                onClick={() => setShowAccommodationForm(false)}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleAccommodationSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  id="fullName"
                  value={accommodationRequest.fullName}
                  onChange={(e) =>
                    setAccommodationRequest({
                      ...accommodationRequest,
                      fullName: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer text-sm md:text-base"
                  required
                />
                <label
                  htmlFor="fullName"
                  className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                >
                  Full Name
                </label>
              </div>

              <div className="relative">
                <input
                  type="tel"
                  id="phoneNumber"
                  value={accommodationRequest.phoneNumber}
                  onChange={(e) =>
                    setAccommodationRequest({
                      ...accommodationRequest,
                      phoneNumber: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer text-sm md:text-base"
                  required
                />
                <label
                  htmlFor="phoneNumber"
                  className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                >
                  Phone Number
                </label>
              </div>

              <div className="relative">
                <textarea
                  id="disabilityNature"
                  value={accommodationRequest.disabilityNature}
                  onChange={(e) =>
                    setAccommodationRequest({
                      ...accommodationRequest,
                      disabilityNature: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer min-h-[80px] md:min-h-[100px] text-sm md:text-base"
                  required
                />
                <label
                  htmlFor="disabilityNature"
                  className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                >
                  Nature of Disability
                </label>
              </div>

              <div className="relative">
                <textarea
                  id="requestDetails"
                  value={accommodationRequest.requestDetails}
                  onChange={(e) =>
                    setAccommodationRequest({
                      ...accommodationRequest,
                      requestDetails: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 pt-5 md:pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer min-h-[80px] md:min-h-[100px] text-sm md:text-base"
                  required
                />
                <label
                  htmlFor="requestDetails"
                  className="absolute left-3 md:left-4 top-1 md:top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                >
                  Accommodation Request
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAccommodationForm(false)}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-2 bg-blue-500/80 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2 text-sm md:text-base"
                >
                  <Plus size={18} />
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Follow-up Message Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-4 md:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-white">
                Important Information About Accommodation Requests
              </h3>
              <button
                onClick={() => setShowInfoModal(false)}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            <div className="space-y-3 md:space-y-4 text-slate-300 text-sm md:text-base">
              <p>
                1. <strong>Verify the Disability:</strong>{" "}
                {organizationData.name} has the right to verify the existence of
                a disability if it's not apparent or already known.
              </p>
              <p>
                2. <strong>Request for Documentation:</strong>{" "}
                {organizationData.name} has the right to request reliable
                documentation verifying the accommodation's need.
              </p>
              <p>
                3. <strong>Financial Ability:</strong> {organizationData.name}{" "}
                has the right to verify the resident's financial ability to
                cover the costs associated with the accommodation (if any).
              </p>
              <p>
                4. <strong>Safety Considerations:</strong>{" "}
                {organizationData.name} may deny a requested accommodation if it
                poses a direct threat to the health or safety of others.
              </p>
              <p>
                5. <strong>Reasonableness of Request:</strong>{" "}
                {organizationData.name} can refuse a request if it imposes an
                undue financial or administrative burden.
              </p>
              <p>
                6. <strong>Refusal of Voluntary Services:</strong>{" "}
                {organizationData.name} can refuse to provide services or
                benefits that are not required by law.
              </p>
            </div>

            <div className="flex justify-end mt-4 md:mt-6">
              <button
                onClick={() => {
                  setShowInfoModal(false);
                  setShowAccommodationForm(true);
                }}
                className="px-3 py-2 bg-blue-500/80 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm md:text-base"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
      {isMyInfoModalOpen && (
        /* ⬇️  Added overflow-y-auto + overscroll-contain here */
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto overscroll-contain">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-blue-500/10 rounded-lg">
                  <UserCircle className="text-blue-400" size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-white">
                  My Information
                </h3>
              </div>
              {/* ⬇️  Release body scroll when closing */}
              <button
                onClick={() => {
                  document.body.style.overflow = "";
                  setIsMyInfoModalOpen(false);
                }}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
              {isLoadingMyInfo ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                </div>
              ) : editableProfile ? (
                <>
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h4 className="text-base md:text-lg text-white font-medium">
                      Personal Information
                    </h4>

                    <div className="relative">
                      <input
                        type="date"
                        value={editableProfile.dob || ""}
                        onChange={(e) => handleChange("dob", e.target.value)}
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Date of Birth
                      </label>
                    </div>

                    <div className="relative">
                      <input
                        type="email"
                        value={editableProfile.email || ""}
                        onChange={(e) => handleChange("email", e.target.value)}
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Email
                      </label>
                    </div>

                    <div className="relative">
                      <select
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                        id="workStatus"
                        name="workStatus"
                        value={editableProfile.workStatus || ""}
                        onChange={(e) => handleWorkStatusChange(e.target.value)}
                      >
                        <option value="">Select work status</option>
                        <option value="Full-Time">Full-Time</option>
                        <option value="Part-Time">Part-Time</option>
                        <option value="Retired">Retired</option>
                        <option value="Unemployed">Unemployed</option>
                      </select>
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Work Status
                      </label>
                    </div>

                    {/* Employment History */}
                    {editableProfile.employmentHistory &&
                      editableProfile.employmentHistory.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-slate-300 text-sm mb-2">
                            Employment History
                          </h4>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {editableProfile.employmentHistory.map(
                              (record, index) => (
                                <div
                                  key={index}
                                  className="bg-slate-800/50 rounded-lg p-3 text-sm border border-slate-700"
                                >
                                  {editingEmploymentIndex === index ? (
                                    // Edit mode
                                    <div className="space-y-3">
                                      <div>
                                        <select
                                          value={editingEmploymentRecord.status}
                                          onChange={(e) =>
                                            setEditingEmploymentRecord({
                                              ...editingEmploymentRecord,
                                              status: e.target.value,
                                            })
                                          }
                                          className="w-full bg-slate-700 rounded px-2 py-1 text-white border border-slate-600"
                                        >
                                          <option value="Full-Time">
                                            Full-Time
                                          </option>
                                          <option value="Part-Time">
                                            Part-Time
                                          </option>
                                          <option value="Unemployed">
                                            Unemployed
                                          </option>
                                          <option value="Retired">
                                            Retired
                                          </option>
                                        </select>
                                      </div>

                                      <div>
                                        <input
                                          type="date"
                                          value={editingEmploymentRecord.date}
                                          onChange={(e) =>
                                            setEditingEmploymentRecord({
                                              ...editingEmploymentRecord,
                                              date: e.target.value,
                                            })
                                          }
                                          className="w-full bg-slate-700 rounded px-2 py-1 text-white border border-slate-600"
                                        />
                                      </div>

                                      {editingEmploymentRecord.status !==
                                        "Unemployed" && (
                                        <div>
                                          <input
                                            type="text"
                                            value={
                                              editingEmploymentRecord.employer ||
                                              ""
                                            }
                                            onChange={(e) =>
                                              setEditingEmploymentRecord({
                                                ...editingEmploymentRecord,
                                                employer: e.target.value,
                                              })
                                            }
                                            placeholder="Employer"
                                            className="w-full bg-slate-700 rounded px-2 py-1 text-white border border-slate-600"
                                          />
                                        </div>
                                      )}

                                      <div>
                                        <textarea
                                          value={
                                            editingEmploymentRecord.notes || ""
                                          }
                                          onChange={(e) =>
                                            setEditingEmploymentRecord({
                                              ...editingEmploymentRecord,
                                              notes: e.target.value,
                                            })
                                          }
                                          placeholder="Notes"
                                          className="w-full bg-slate-700 rounded px-2 py-1 text-white border border-slate-600"
                                          rows={2}
                                        />
                                      </div>

                                      <div className="flex justify-end gap-2">
                                        <button
                                          onClick={handleCancelEmploymentEdit}
                                          className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={handleSaveEmploymentEdit}
                                          className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
                                        >
                                          Save
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    // View mode
                                    <>
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-blue-400 font-medium">
                                          {record.status}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-slate-400">
                                            {new Date(
                                              record.date
                                            ).toLocaleDateString()}
                                          </span>
                                          <button
                                            onClick={() =>
                                              handleEditEmploymentRecord(index)
                                            }
                                            className="text-slate-400 hover:text-white"
                                          >
                                            <Edit2 size={14} />
                                          </button>
                                        </div>
                                      </div>

                                      {record.employer && (
                                        <div className="text-slate-300">
                                          Employer: {record.employer}
                                        </div>
                                      )}
                                      {record.notes && (
                                        <div className="text-slate-400 text-xs mt-1">
                                          {record.notes}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-4">
                    <h4 className="text-base md:text-lg text-white font-medium">
                      Emergency Contact
                    </h4>

                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.ecFirstName || ""}
                        onChange={(e) =>
                          handleChange("ecFirstName", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        First Name
                      </label>
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.ecLastName || ""}
                        onChange={(e) =>
                          handleChange("ecLastName", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Last Name
                      </label>
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.ecRelationship || ""}
                        onChange={(e) =>
                          handleChange("ecRelationship", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Relationship
                      </label>
                    </div>

                    <div className="relative">
                      <input
                        type="tel"
                        value={editableProfile.ecPhoneNumber || ""}
                        onChange={(e) =>
                          handleChange("ecPhoneNumber", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Phone Number
                      </label>
                    </div>
                  </div>

                  {/* Medication */}
                  <div className="space-y-4">
                    <h4 className="text-base md:text-lg text-white font-medium">
                      Medication
                    </h4>

                    <div className="relative">
                      <select
                        value={editableProfile.isMAT ? "Yes" : "No"}
                        onChange={(e) =>
                          handleMATStatusChange(e.target.value === "Yes")
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      >
                        <option value="">Select an option</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        MAT?
                      </label>
                    </div>
                    {editableProfile?.matHistory &&
                      editableProfile.matHistory.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-slate-300 text-sm mb-2">
                            MAT History
                          </h4>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {editableProfile.matHistory.map((record, index) => (
                              <div
                                key={index}
                                className="bg-slate-800/50 rounded-lg p-3 text-sm border border-slate-700"
                              >
                                <div className="flex justify-between mb-1">
                                  <span className="text-purple-400 font-medium">
                                    {record.status}
                                  </span>
                                  <span className="text-slate-400">
                                    {new Date(record.date).toLocaleDateString()}
                                  </span>
                                </div>
                                {record.notes && (
                                  <div className="text-slate-400 text-xs mt-1">
                                    {record.notes}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    {editableProfile.medications?.map((medication, index) => (
                      <div key={index} className="grid grid-cols-2 gap-4">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={medication.name}
                            onChange={(e) =>
                              handleMedicationNameInput(e, index)
                            }
                            onBlur={() =>
                              setTimeout(() => {
                                setMedicationSuggestions([]);
                                setActiveMedicationIndex(null);
                              }, 200)
                            }
                            className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors"
                            placeholder="Medication Name"
                          />

                          {medicationSuggestions.length > 0 &&
                            activeMedicationIndex === index && (
                              <div className="absolute top-full left-0 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-50">
                                {medicationSuggestions.map((s, i) => (
                                  <div
                                    key={i}
                                    onClick={() =>
                                      handleSelectSuggestion(s, index)
                                    }
                                    className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-white"
                                  >
                                    <div className="font-semibold">
                                      {s.brandName} ({s.genericName})
                                    </div>
                                    <div className="text-sm text-slate-400">
                                      {s.type}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>

                        <div className="relative">
                          <input
                            type="text"
                            value={medication.dose || ""}
                            onChange={(e) =>
                              handleMedicationChange(
                                index,
                                "dose",
                                e.target.value
                              )
                            }
                            className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                          />
                          <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                            Dose
                          </label>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={addMedication}
                      className="px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Plus size={20} />
                      Add Medication
                    </button>
                  </div>

                  {/* Recovery Info */}
                  <div className="space-y-4">
                    <h4 className="text-base md:text-lg text-white font-medium">
                      Recovery Info
                    </h4>

                    <div className="relative">
                      <input
                        type="date"
                        value={editableProfile.sobrietyDate || ""}
                        onChange={(e) =>
                          handleChange("sobrietyDate", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Sobriety Date
                      </label>
                    </div>

                    <div className="relative">
                      <label className="block text-xs text-slate-400 mb-2">
                        Drug of Choice (Max 3)
                      </label>
                      <Select
                        className="mt-1"
                        isMulti
                        options={[
                          { value: "Heroin", label: "Heroin" },
                          { value: "Meth", label: "Meth" },
                          { value: "THC", label: "THC" },
                          { value: "Alcohol", label: "Alcohol" },
                          { value: "Crack", label: "Crack" },
                          { value: "Amphetamines", label: "Amphetamines" },
                          { value: "Cocaine", label: "Cocaine" },
                          { value: "Cough Syrup", label: "Cough Syrup" },
                        ]}
                        value={
                          editableProfile.drugOfChoice &&
                          Array.isArray(editableProfile.drugOfChoice)
                            ? editableProfile.drugOfChoice.map((choice) => ({
                                value: choice,
                                label: choice,
                              }))
                            : []
                        }
                        onChange={(selectedOptions) => {
                          // Limit to max 3 selections
                          if (selectedOptions.length <= 3) {
                            const newChoices = selectedOptions.map(
                              (option) => option.value
                            );
                            handleChange("drugOfChoice", newChoices);
                          }
                        }}
                        isOptionDisabled={() =>
                          editableProfile.drugOfChoice?.length >= 3
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: "rgba(30, 41, 59, 0.5)",
                            borderColor: "rgb(71, 85, 105)",
                            minHeight: "40px",
                            "&:hover": {
                              borderColor: "rgb(59, 130, 246)",
                            },
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            position: "absolute",
                            backgroundColor: "rgb(30, 41, 59)",
                            border: "1px solid rgb(71, 85, 105)",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? "rgb(59, 130, 246)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: "rgba(59, 130, 246, 0.1)",
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: "rgba(59, 130, 246, 0.1)",
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: "rgb(59, 130, 246)",
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: "rgb(59, 130, 246)",
                            "&:hover": {
                              backgroundColor: "rgba(59, 130, 246, 0.2)",
                              color: "rgb(59, 130, 246)",
                            },
                          }),
                        }}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  {/* Legal */}
                  <div className="space-y-4">
                    <h4 className="text-base md:text-lg text-white font-medium">
                      Legal
                    </h4>

                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.poFirstName || ""}
                        onChange={(e) =>
                          handlePOFieldChange("poFirstName", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        PO First Name
                      </label>
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.poLastName || ""}
                        onChange={(e) =>
                          handlePOFieldChange("poLastName", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        PO Last Name
                      </label>
                    </div>
                    {editableProfile?.probationHistory &&
                      editableProfile.probationHistory.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-slate-300 text-sm mb-2">
                            Probation/Parole History
                          </h4>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {editableProfile.probationHistory.map(
                              (record, index) => (
                                <div
                                  key={index}
                                  className="bg-slate-800/50 rounded-lg p-3 text-sm border border-slate-700"
                                >
                                  <div className="flex justify-between mb-1">
                                    <span className="text-indigo-400 font-medium">
                                      {record.status}
                                    </span>
                                    <span className="text-slate-400">
                                      {new Date(
                                        record.date
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {record.notes && (
                                    <div className="text-slate-400 text-xs mt-1">
                                      {record.notes}
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.county || ""}
                        onChange={(e) => handleChange("county", e.target.value)}
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        County
                      </label>
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        value={editableProfile.pendingCharges || ""}
                        onChange={(e) =>
                          handleChange("pendingCharges", e.target.value)
                        }
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      />
                      <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                        Pending Charges
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-20 text-slate-400">
                  Failed to load your information. Please try again.
                </div>
              )}
            </div>

            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  document.body.style.overflow = "";
                  setIsMyInfoModalOpen(false);
                }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProfile}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      {showEmploymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              Update Employment Status
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="date"
                  value={newEmploymentDate}
                  onChange={(e) => setNewEmploymentDate(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                  First Day of Work
                </label>
              </div>

              {pendingWorkStatusChange !== "Unemployed" && (
                <div className="relative">
                  <input
                    type="text"
                    value={employer}
                    onChange={(e) => setEmployer(e.target.value)}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                  />
                  <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                    Employer Name (Optional)
                  </label>
                </div>
              )}

              <div className="relative">
                <textarea
                  value={employmentNotes}
                  onChange={(e) => setEmploymentNotes(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer min-h-[100px]"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                  Additional Notes (Optional)
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelWorkStatusChange}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmWorkStatusChange}
                  disabled={!newEmploymentDate}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Payment History Modal */}
      {paymentHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-900/95 backdrop-blur-sm z-[102]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <DollarSign className="text-emerald-400" size={20} />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  Payment History
                </h3>
              </div>
              <button
                onClick={() => setPaymentHistoryModalOpen(false)}
                className="relative z-[103] p-2 hover:bg-slate-800 rounded-lg transition-colors touch-auto"
              >
                <X className="text-slate-400 hover:text-slate-300" size={20} />
              </button>
            </div>

            {/* Current Balance and Rent Due Display */}
            <div className="p-4 md:p-6 bg-slate-800/50 border-b border-slate-700">
              <div className="flex flex-col md:flex-row md:justify-around text-center gap-4">
                {/* Current Balance */}
                <div>
                  <p className="text-slate-400 text-sm mb-1">Current Balance</p>
                  <p
                    className={`text-3xl font-bold ${
                      residentData?.balance < 0
                        ? "text-red-400"
                        : "text-emerald-400"
                    }`}
                  >
                    $
                    {residentData?.balance !== undefined
                      ? residentData.balance.toFixed(2)
                      : "0.00"}
                  </p>
                </div>

                {/* Next Rent Due */}
                <div>
                  <p className="text-slate-400 text-sm mb-1">Housing Fee Due</p>
                  <p className="text-xl font-semibold text-slate-300">
                    {residentData?.rentDueDate
                      ? (typeof residentData.rentDueDate.toDate === "function"
                          ? residentData.rentDueDate.toDate()
                          : new Date(residentData.rentDueDate)
                        ).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Sticky Column Headers - Hidden on mobile, visible on larger screens */}
              <div className="sticky top-0 px-6 pt-6 pb-2 bg-slate-900/95 backdrop-blur-sm z-[99] hidden md:block">
                <div className="grid grid-cols-5 gap-4 font-medium">
                  <p className="text-center text-slate-300">Date</p>
                  <p className="text-center text-slate-300">Type</p>
                  <p className="text-center text-slate-300">Amount</p>
                  <p className="text-center text-slate-300">New Balance</p>
                  <p className="text-center text-slate-300">Receipt</p>
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="px-6 pb-6 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800 overscroll-contain">
                <div className="space-y-4 pt-4">
                  {" "}
                  {/* Added more padding-top here */}
                  {/* Payment Rows */}
                  {paymentHistory
                    .sort((a, b) => {
                      // Handle different date formats safely
                      let dateA, dateB;

                      if (
                        a.paymentDate &&
                        typeof a.paymentDate.toDate === "function"
                      ) {
                        dateA = a.paymentDate.toDate();
                      } else if (a.paymentDate instanceof Date) {
                        dateA = a.paymentDate;
                      } else {
                        dateA = new Date(a.paymentDate || 0);
                      }

                      if (
                        b.paymentDate &&
                        typeof b.paymentDate.toDate === "function"
                      ) {
                        dateB = b.paymentDate.toDate();
                      } else if (b.paymentDate instanceof Date) {
                        dateB = b.paymentDate;
                      } else {
                        dateB = new Date(b.paymentDate || 0);
                      }

                      return dateB - dateA; // Sort descending (newest first)
                    })
                    .map((payment) => (
                      <details
                        key={payment.id}
                        className="group relative z-[100]"
                      >
                        <summary className="md:grid md:grid-cols-5 flex flex-col gap-2 md:gap-4 items-start md:items-center cursor-pointer p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors touch-auto">
                          {/* Mobile view - Show labels with data */}
                          <div className="flex justify-between items-center w-full md:hidden">
                            <div className="space-y-2 w-full">
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Date:
                                </span>
                                <p className="text-slate-300">
                                  {payment.paymentDate &&
                                  typeof payment.paymentDate.toDate ===
                                    "function"
                                    ? payment.paymentDate
                                        .toDate()
                                        .toLocaleDateString()
                                    : payment.paymentDate instanceof Date
                                    ? payment.paymentDate.toLocaleDateString()
                                    : new Date(
                                        payment.paymentDate
                                      ).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Type:
                                </span>
                                <p className="text-slate-300">
                                  {payment.paymentType || "Cash"}
                                </p>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Amount:
                                </span>
                                <p className="text-emerald-400 font-medium">
                                  ${(payment.paymentAmount || 0).toFixed(2)}
                                </p>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Balance:
                                </span>
                                <p
                                  className={`font-medium ${
                                    (payment.newBalance || 0) < 0
                                      ? "text-red-400"
                                      : "text-emerald-400"
                                  }`}
                                >
                                  $
                                  {(Number(payment.newBalance) || 0).toFixed(2)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadReceipt(payment);
                              }}
                              className="bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 p-1.5 rounded-lg transition-all ml-4"
                              title="Download Receipt"
                            >
                              <Download size={16} />
                            </button>
                          </div>

                          {/* Desktop view - Grid layout */}
                          <p className="text-center text-slate-300 hidden md:block">
                            {payment.paymentDate &&
                            typeof payment.paymentDate.toDate === "function"
                              ? payment.paymentDate
                                  .toDate()
                                  .toLocaleDateString()
                              : payment.paymentDate instanceof Date
                              ? payment.paymentDate.toLocaleDateString()
                              : new Date(
                                  payment.paymentDate
                                ).toLocaleDateString()}
                          </p>
                          <p className="text-center text-slate-300 hidden md:block">
                            {payment.paymentType || "Cash"}
                          </p>
                          <p className="text-center text-emerald-400 font-medium hidden md:block">
                            ${(payment.paymentAmount || 0).toFixed(2)}
                          </p>
                          <p
                            className={`text-center font-medium hidden md:block ${
                              (payment.newBalance || 0) < 0
                                ? "text-red-400"
                                : "text-emerald-400"
                            }`}
                          >
                            ${(Number(payment.newBalance) || 0).toFixed(2)}
                          </p>
                          <div className="flex justify-center hidden md:flex">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadReceipt(payment);
                              }}
                              className="bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 p-1.5 rounded-lg transition-all"
                              title="Download Receipt"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </summary>

                        {/* Expanded details section */}
                        <div className="mt-2 p-4 bg-slate-800/30 rounded-lg border border-slate-700 space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Resident Name */}
                            {payment.residentName && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Resident
                                </p>
                                <p className="text-slate-300">
                                  {payment.residentName}
                                </p>
                              </div>
                            )}

                            {/* Invoice Number - Always show for invoice payments */}
                            {payment.paymentType === "Invoice" && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Invoice Number
                                </p>
                                <p className="text-slate-300">
                                  {payment.invoiceNumber || "N/A"}
                                </p>
                              </div>
                            )}

                            {/* Invoice Week */}
                            {payment.invoiceWeek && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Invoice Week
                                </p>
                                <p className="text-slate-300">
                                  {payment.invoiceWeek}
                                </p>
                              </div>
                            )}

                            {/* Funding Source */}
                            {payment.fundingSource && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Funding Source
                                </p>
                                <p className="text-slate-300">
                                  {payment.fundingSource}
                                </p>
                              </div>
                            )}

                            {/* Payment Note */}
                            {payment.paymentNote && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Payment Note
                                </p>
                                <p className="text-slate-300">
                                  {payment.paymentNote}
                                </p>
                              </div>
                            )}

                            {/* Previous Balance */}
                            {typeof payment.previousBalance !== "undefined" && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Previous Balance
                                </p>
                                <p
                                  className={`font-medium ${
                                    payment.previousBalance < 0
                                      ? "text-red-400"
                                      : "text-emerald-400"
                                  }`}
                                >
                                  $
                                  {(
                                    Number(payment.previousBalance) || 0
                                  ).toFixed(2)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </details>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Documents Modal */}
      {documentsModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-blue-500/10 rounded-lg">
                  <FileText className="text-blue-400" size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-white">
                  Documents
                </h3>
              </div>
              <button
                onClick={() => setDocumentsModalOpen(false)}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-slate-700 overflow-x-auto">
              <div className="flex">
                <button
                  onClick={() => setActiveDocTab("pending")}
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${
                    activeDocTab === "pending"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  {pendingDocuments.length > 0
                    ? `Pending (${pendingDocuments.length})`
                    : "Pending"}
                </button>
                <button
                  onClick={() => setActiveDocTab("completed")}
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${
                    activeDocTab === "completed"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setActiveDocTab("other")}
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${
                    activeDocTab === "other"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  Other Documents
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {/* Pending Documents Tab */}
              {activeDocTab === "pending" && (
                <>
                  {pendingDocuments.length > 0 ? (
                    <div className="space-y-3">
                      {pendingDocuments.map((doc) => (
                        <div
                          key={doc.id}
                          className="bg-slate-800/30 rounded-xl p-3 md:p-4 border border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
                          onClick={() => handleOpenDocument(doc, false)}
                        >
                          <div className="flex justify-between items-center flex-wrap gap-2">
                            <div className="flex items-center gap-2 md:gap-3">
                              <FileText className="text-red-400" size={16} />
                              <div>
                                <p className="text-white text-sm md:text-base font-medium">
                                  {doc.name}
                                </p>
                                {doc.description && (
                                  <p className="text-slate-400 text-xs md:text-sm mt-1">
                                    {doc.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="bg-red-500/20 text-red-300 text-xs px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
                              Required
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 md:py-12">
                      <div className="bg-slate-800/30 p-4 md:p-6 rounded-xl">
                        <FileText
                          className="text-slate-500 mx-auto mb-3 md:mb-4"
                          size={32}
                        />
                        <p className="text-slate-400 text-sm md:text-base">
                          No pending documents
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Completed Documents Tab */}
              {activeDocTab === "completed" && (
                <>
                  {completedForms.length > 0 ? (
                    <div className="space-y-3">
                      {completedForms.map((doc) => {
                        // If it's a file upload
                        if (doc.type === "fileUpload") {
                          // Convert createdAt (if available) to Date
                          const uploadedDate = doc.createdAt?.toDate
                            ? doc.createdAt.toDate()
                            : null;

                          return (
                            <div
                              key={doc.id}
                              className="bg-slate-800/30 rounded-xl p-3 md:p-4 border border-slate-700/50 
                           hover:bg-slate-800/50 cursor-pointer transition-colors"
                              // For a file upload, open in new tab on click
                              onClick={() => window.open(doc.fileURL, "_blank")}
                            >
                              <div className="flex justify-between items-center flex-wrap gap-2">
                                <div className="flex items-center gap-2 md:gap-3">
                                  <FileText
                                    className="text-green-400"
                                    size={16}
                                  />
                                  <div>
                                    <p className="text-white text-sm md:text-base font-medium">
                                      {doc.saveAs || doc.fileName}
                                    </p>
                                    <p className="text-slate-400 text-xs md:text-sm mt-1">
                                      Uploaded:{" "}
                                      {uploadedDate
                                        ? uploadedDate.toLocaleDateString()
                                        : "Unknown"}
                                    </p>
                                  </div>
                                </div>
                                <div className="bg-green-500/20 text-green-300 text-xs px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
                                  File Upload
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Otherwise, treat it as a form submission (including applications)
                        return (
                          <div
                            key={doc.id}
                            className="bg-slate-800/30 rounded-xl p-3 md:p-4 border border-slate-700/50 
                         hover:bg-slate-800/50 cursor-pointer transition-colors"
                            onClick={() => handleOpenDocument(doc, true)}
                          >
                            <div className="flex justify-between items-center flex-wrap gap-2">
                              <div className="flex items-center gap-2 md:gap-3">
                                <FileText
                                  className="text-green-400"
                                  size={16}
                                />
                                <div>
                                  <p className="text-white text-sm md:text-base font-medium">
                                    {doc.formName}
                                  </p>
                                  <p className="text-slate-400 text-xs md:text-sm mt-1">
                                    Completed:{" "}
                                    {doc.dateSubmitted?.seconds
                                      ? new Date(
                                          doc.dateSubmitted.seconds * 1000
                                        ).toLocaleDateString()
                                      : "Invalid Date"}
                                  </p>
                                </div>
                              </div>
                              <div className="bg-green-500/20 text-green-300 text-xs px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
                                Completed
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 md:py-12">
                      <div className="bg-slate-800/30 p-4 md:p-6 rounded-xl">
                        <FileText
                          className="text-slate-500 mx-auto mb-3 md:mb-4"
                          size={32}
                        />
                        <p className="text-slate-400 text-sm md:text-base">
                          No completed documents
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Other Documents Tab */}
              {activeDocTab === "other" && (
                <>
                  {allForms.filter((form) => form.isResidentDocument).length >
                  0 ? (
                    <div className="space-y-3">
                      {allForms
                        .filter((form) => form.isResidentDocument)
                        .map((doc) => (
                          <div
                            key={doc.id}
                            className="bg-slate-800/30 rounded-xl p-3 md:p-4 border border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
                            onClick={() => handleOpenDocument(doc, false)}
                          >
                            <div className="flex justify-between items-center flex-wrap gap-2">
                              <div className="flex items-center gap-2 md:gap-3">
                                <FileText className="text-blue-400" size={16} />
                                <div>
                                  <p className="text-white text-sm md:text-base font-medium">
                                    {doc.name}
                                  </p>
                                  {doc.description && (
                                    <p className="text-slate-400 text-xs md:text-sm mt-1">
                                      {doc.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="bg-blue-500/20 text-blue-300 text-xs px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
                                Available
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 md:py-12">
                      <div className="bg-slate-800/30 p-4 md:p-6 rounded-xl">
                        <FileText
                          className="text-slate-500 mx-auto mb-3 md:mb-4"
                          size={32}
                        />
                        <p className="text-slate-400 text-sm md:text-base">
                          No other documents available
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Form Submission View Modal */}
      {viewingFormSubmission && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-blue-500/10 rounded-lg">
                  <ClipboardList className="text-blue-400" size={18} />
                </div>
                <h3 className="text-base md:text-xl font-semibold text-white truncate">
                  {viewingFormSubmission.formName}
                </h3>
              </div>
              <button
                onClick={() => setViewingFormSubmission(null)}
                className="p-1.5 md:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="space-y-4 md:space-y-6">
                {/* Metadata */}
                <div className="mb-3 md:mb-6 p-3 md:p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-slate-400 text-xs md:text-sm">
                    Submitted on:{" "}
                    {viewingFormSubmission.dateSubmitted
                      ? new Date(
                          viewingFormSubmission.dateSubmitted.seconds * 1000
                        ).toLocaleString()
                      : "Unknown date"}
                  </p>
                  {viewingFormSubmission.submittedBy && (
                    <p className="text-slate-400 text-xs md:text-sm mt-1">
                      Submitted by:{" "}
                      {viewingFormSubmission.submittedBy.email || "Unknown"}
                    </p>
                  )}
                </div>

                {/* Form Fields - Modified to handle different data structures */}
                {viewingFormSubmission.responses &&
                viewingFormSubmission.elementOrder ? (
                  // Use the preserved order if available
                  viewingFormSubmission.elementOrder.map((uniqueId) => {
                    // Get metadata for this element
                    const metadata =
                      viewingFormSubmission.elementMetadata?.[uniqueId];
                    // Get response value for this element
                    const value = viewingFormSubmission.responses?.[uniqueId];

                    if (!metadata) return null;

                    return (
                      <div
                        key={uniqueId}
                        className="p-3 md:p-4 bg-slate-800/50 rounded-lg mb-3 md:mb-4"
                      >
                        <label className="block text-xs md:text-sm font-medium text-slate-400 mb-1">
                          {metadata.label || uniqueId}
                        </label>

                        {/* Render field value based on type */}
                        {metadata.type === "textBox" ||
                        metadata.type === "input" ? (
                          <div className="text-white text-sm md:text-base">
                            {String(value || "")}
                          </div>
                        ) : metadata.type === "checkbox" ||
                          metadata.type === "yesNo" ? (
                          <div className="text-white text-sm md:text-base">
                            {value === true ? "Yes" : "No"}
                          </div>
                        ) : metadata.type === "date" &&
                          value &&
                          typeof value === "object" &&
                          "seconds" in value ? (
                          <div className="text-white text-sm md:text-base">
                            {new Date(
                              value.seconds * 1000
                            ).toLocaleDateString()}
                          </div>
                        ) : metadata.type === "signature" ? (
                          <img
                            src={value}
                            alt="Signature"
                            className="max-w-full h-auto border border-slate-700 rounded-lg"
                          />
                        ) : Array.isArray(value) ? (
                          <div className="text-white text-sm md:text-base">
                            {value.map((item, i) => (
                              <div key={i}>• {item}</div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-white text-sm md:text-base">
                            {String(value || "")}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  // Fallback for other data structures
                  <div className="p-3 md:p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-white text-sm md:text-base mb-4">
                      This form was completed, but has a different structure
                      than expected.
                    </p>
                    {/* Show all key-value pairs excluding internal fields */}
                    {Object.entries(viewingFormSubmission)
                      .filter(
                        ([key]) =>
                          ![
                            "id",
                            "formId",
                            "residentId",
                            "formName",
                            "dateSubmitted",
                            "submittedBy",
                            "elementOrder",
                            "elementMetadata",
                            "responses",
                            "residentFullName",
                          ].includes(key)
                      )
                      .map(([key, value]) => (
                        <div key={key} className="mb-3">
                          <label className="block text-xs md:text-sm font-medium text-slate-400 mb-1">
                            {key}
                          </label>
                          <div className="text-white text-sm md:text-base">
                            {typeof value === "object" && value !== null
                              ? JSON.stringify(value)
                              : String(value || "")}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setViewingFormSubmission(null)}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors text-sm md:text-base"
              >
                Close
              </button>
              <button
                onClick={() => generateFormPDF(viewingFormSubmission)}
                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2 text-sm md:text-base"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
      {showMATStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              Update MAT Status
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="date"
                  value={newMATDate}
                  onChange={(e) => setNewMATDate(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors">
                  Date Ended MAT
                </label>
              </div>

              <div className="relative">
                <textarea
                  value={matNotes}
                  onChange={(e) => setMATNotes(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer min-h-[100px]"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors">
                  Additional Notes (Optional)
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelMATStatusChange}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMATStatusChange}
                  disabled={!newMATDate}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Probation Status Change Modal */}
      {showProbationStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              Record End of Probation/Parole
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="date"
                  value={newProbationDate}
                  onChange={(e) => setNewProbationDate(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors">
                  Date Ended Probation/Parole
                </label>
              </div>

              <div className="relative">
                <textarea
                  value={probationNotes}
                  onChange={(e) => setProbationNotes(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer min-h-[100px]"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors">
                  Additional Notes (Optional)
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelProbationStatusChange}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmProbationStatusChange}
                  disabled={!newProbationDate}
                  className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewingForm && selectedForm && (
        <FormRenderer
          form={selectedForm}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setViewingForm(false);
            setSelectedForm(null);
          }}
        />
      )}
      {/* Mobile Action Button Fixed at Bottom */}
      <div className="fixed bottom-4 right-4 lg:hidden z-20">
        <button
          onClick={toggleMobileMenu}
          className="bg-blue-500 hover:bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
        >
          {mobileMenuOpen ? <X size={24} /> : <ChevronRight size={24} />}
        </button>
      </div>
    </div>
  );
};

export default ResidentDashboard;
